import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { Users, Plus, Edit, Pencil, Trash2, Eye, Loader2, Upload, FileText, ClipboardCheck, MoreHorizontal, CheckCircle, Settings, X, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Search, RotateCw, RotateCcw, Download, Check, Crop, XCircle, Link2, Banknote, Wallet, PercentSquare, HandHelping, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { CoordinatorSettings } from './CoordinatorSettings';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SaudiRiyalIcon } from '@/components/ui/saudi-riyal-icon';
import { useTreatmentCategories } from '@/lib/firebase/queries/treatmentCategories';
import { assertCategoryMatch } from '@/lib/firebase/queries/treatmentSupport';
import { useLinks } from '@/lib/firebase/queries/links';
import { useDonations } from '@/lib/firebase/queries/donations';
import { useMarketingPayments } from '@/lib/firebase/queries/marketingPayments';
import { useVatRefunds } from '@/lib/firebase/queries/vatRefunds';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { TreatmentSupportRequest, TreatmentSupportStatus, DonationEntry, CaseUpdate, TreatmentIdType, TreatmentGender, TreatmentMaritalStatus, TreatmentEducationLevel, TreatmentWorkSector, TreatmentHousingType, CashbackEntry, CashbackCaseAllocation, DonationTransaction } from '@/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTreatmentSupportRequests } from '@/hooks/use-treatment-support';
import { MedicalReportUploader, PendingReport } from '@/components/shared/MedicalReportUploader';
import { HijriDatePicker } from '@/components/shared/HijriDatePicker';
import { CategorySelect } from '@/components/shared/CategorySelect';
import { InvoiceUploader, PendingInvoice } from '@/components/shared/InvoiceUploader';
import { ProgressReportUploader, type PendingProgressReport } from '@/components/shared/ProgressReportUploader';
import { canStartTreatment, shouldOfferStartTreatment } from '@/lib/services/treatmentWorkflow';
import { useMedicalProviders } from '@/lib/firebase/queries/medicalProviders';
import { useCashbacks, addCashback } from '@/lib/firebase/queries/cashbacks';
import { toast } from 'sonner';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { storage, db } from '@/lib/firebase/config';

const STATUS_COLORS: Record<TreatmentSupportStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-orange-100 text-orange-800',
  pending_approval: 'bg-purple-100 text-purple-800',
  approved: 'bg-green-100 text-green-800',
  approved_treatment: 'bg-teal-100 text-teal-800',
  treatment_started: 'bg-indigo-100 text-indigo-800',
  rejected: 'bg-red-100 text-red-800',
  pending_invoices: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
};

const CATEGORY_COLORS = [
  'bg-blue-100 text-blue-800',
  'bg-violet-100 text-violet-800',
  'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-800',
  'bg-teal-100 text-teal-800',
  'bg-indigo-100 text-indigo-800',
  'bg-orange-100 text-orange-800',
  'bg-cyan-100 text-cyan-800',
  'bg-pink-100 text-pink-800',
  'bg-lime-100 text-lime-800',
  'bg-fuchsia-100 text-fuchsia-800',
  'bg-sky-100 text-sky-800',
];

export function CoordinatorDashboard() {
  const { t, dir, language } = useLanguage();
  const { currentUser } = useAuth();
  const ALLOWED_TOP_TABS = ['pending', 'approval', 'approved', 'approvedTreatment', 'treatmentStarted', 'treated', 'completed', 'rejected'] as const;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const topTab = (ALLOWED_TOP_TABS as readonly string[]).includes(urlTab ?? '') ? (urlTab as string) : 'pending';
  const handleTopTabChange = (newTab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', newTab);
    setSearchParams(next, { replace: false });
  };
  const { requests, loading, addRequest, updateRequest, deleteRequest, refreshRequests } = useTreatmentSupportRequests();
  const { categories } = useTreatmentCategories();
  const { providers } = useMedicalProviders();
  const activeProviders = providers.filter(p => p.isActive);
  const { links } = useLinks();
  const { donations } = useDonations();
  console.log(`[perf] CoordinatorDashboard render — donations.length=${donations.length}`);
  const donationSumByLink = useMemo(() => {
    const t0 = performance.now();
    const m: Record<string, number> = {};
    for (const d of donations) m[d.linkId] = (m[d.linkId] || 0) + (d.amount || 0);
    console.log(`[perf] donationSumByLink: ${donations.length} donations in ${(performance.now() - t0).toFixed(1)}ms`);
    return m;
  }, [donations]);
  const donationsByLink = useMemo(() => {
    const t0 = performance.now();
    const m: Record<string, DonationTransaction[]> = {};
    for (const d of donations) (m[d.linkId] = m[d.linkId] || []).push(d);
    for (const k in m) m[k].sort((a, b) => b.date.getTime() - a.date.getTime());
    console.log(`[perf] donationsByLink: ${donations.length} donations in ${(performance.now() - t0).toFixed(1)}ms`);
    return m;
  }, [donations]);
  const donationStartByLink = useMemo(() => {
    const t0 = performance.now();
    const m: Record<string, Date> = {};
    for (const d of donations) if (!m[d.linkId] || d.date < m[d.linkId]) m[d.linkId] = d.date;
    console.log(`[perf] donationStartByLink: ${donations.length} donations in ${(performance.now() - t0).toFixed(1)}ms`);
    return m;
  }, [donations]);
  const { marketingPayments } = useMarketingPayments();
  const { vatRefunds } = useVatRefunds();
  const activeCategories = categories.filter(c => c.isActive);

  const dateLocale = language === 'ar' ? ar : enUS;
  const formatLocalDate = (date: Date, fmt: string = 'PPP') => {
    if (!date || isNaN(date.getTime())) return '—';
    const formatted = format(date, fmt, { locale: dateLocale });
    return formatted.replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
  };

  const translateRole = (role: string) => {
    const key = `common.roles.${role}` as const;
    const translated = t(key);
    return translated !== key ? translated : role;
  };
  
  // View switcher
  const [activeView, setActiveView] = useState<'cases' | 'links' | 'cashbacks' | 'marketing' | 'vat'>('cases');
  const [linksSearchQuery, setLinksSearchQuery] = useState('');

  // Cashback state
  const { cashbacks, loading: cashbacksLoading } = useCashbacks();
  const [isAddCashbackOpen, setIsAddCashbackOpen] = useState(false);
  const [cashbackForm, setCashbackForm] = useState({ providerId: '', totalAmount: '', date: '', notes: '' });
  const [cashbackCases, setCashbackCases] = useState<{ caseId: string; amount: string }[]>([{ caseId: '', amount: '' }]);
  const [cashbackReceiptFile, setCashbackReceiptFile] = useState<File | null>(null);
  const [isSubmittingCashback, setIsSubmittingCashback] = useState(false);

  // Modal states
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addFormStep, setAddFormStep] = useState(1);
  const ADD_TOTAL_STEPS = 5;
  const addStepLabels = [
    t('common.stepLabels.personalInfo'),
    t('common.stepLabels.demographics'),
    t('common.stepLabels.socioeconomic'),
    t('common.stepLabels.medical'),
    t('common.stepLabels.caseStatus'),
  ];
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // Marketing settings
  const [marketingX, setMarketingX] = useState(0.4);
  const [marketingY, setMarketingY] = useState(1);

  // Coordinator permissions (admin-controlled)
  const [coordCanDeleteAssignments, setCoordCanDeleteAssignments] = useState(false);

  const calcCostWithMarketing = (cost: number) => Math.ceil(cost + (cost * marketingX / (marketingY || 1)));

  useEffect(() => {
    const loadMarketingSettings = async () => {
      try {
        const marketingDoc = await getDoc(doc(db, 'settings', 'treatmentMarketing'));
        if (marketingDoc.exists()) {
          const data = marketingDoc.data();
          if (typeof data.x === 'number') setMarketingX(data.x);
          if (typeof data.y === 'number') setMarketingY(data.y);
        }
      } catch (error) {
        console.error('Error loading marketing settings:', error);
      }
    };
    loadMarketingSettings();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'coordPermissions'));
        if (snap.exists()) {
          setCoordCanDeleteAssignments(snap.data().canDeleteAssignments === true);
        }
      } catch (e) {
        console.warn('Failed to load coord permissions:', e);
      }
    })();
  }, []);

  const calcLinkAvailable = (received: number) => {
    // VAT on the marketing share is refundable — it doesn't reduce what
    // the case can spend. Mirrors the allLinks.map formula used by the
    // Projects List display.
    const ms = received * marketingX / ((marketingX + marketingY) || 1);
    return Math.floor(received - Math.ceil(ms));
  };

  const getNetMedicalCost = (req: any): number => {
    const invoiceTotal = Array.isArray(req.invoices)
      ? req.invoices.reduce((s: number, inv: any) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0)
      : 0;
    return invoiceTotal > 0 ? invoiceTotal : (req.cost || 0);
  };

  const getEffectiveCost = (req: any) => {
    // Effective cost = gross donations needed so that, after the donation
    // link's marketing share is deducted, the NET available for the case
    // equals the medical cost. Available-share per donated riyal is
    // Y/(X+Y), so the gross multiplier is its inverse: (X+Y)/Y.
    // For X=46 Y=54 this is ×1.852 (40,000 → 74,074). Applied to all
    // cases regardless of nationality.
    const grossMult = (marketingX + marketingY) / (marketingY || 1);
    const base = (req.invoices && req.invoices.length > 0)
      ? req.invoices.reduce((s: number, inv: any) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0) || (req.cost || 0)
      : (req.cost || 0);
    return Math.ceil(base * grossMult);
  };

  // Usage map keyed by linkId: actualReceived/meta seeded from links collection,
  // allocations accumulated from case donationEntries.
  const linkUsageMap = useMemo(() => {
    const t0 = performance.now();
    const map: Record<string, { actualReceived: number; count: number; totalAllocated: number; nonSaudiAllocated: number; caseInvoiceVAT: number; cases: { id: string; allocated: number; nameAr: string; nameEn: string; idNumber: string; caseNumber?: number }[] }> = {};
    for (const l of links) {
      map[l.id] = { actualReceived: donationSumByLink[l.id] || 0, count: 0, totalAllocated: 0, nonSaudiAllocated: 0, caseInvoiceVAT: 0, cases: [] };
    }
    for (const req of requests) {
      if (!req.donationEntries) continue;
      for (const entry of req.donationEntries) {
        if (!entry.linkId || !map[entry.linkId]) continue;
        const amt = entry.amount || 0;
        map[entry.linkId].totalAllocated += amt;
        if (req.idType !== 'national_id') map[entry.linkId].nonSaudiAllocated += amt;
        if (req.invoices && req.invoices.length > 0) {
          const invTotal = req.invoices.reduce((s, inv) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0);
          const invVAT = req.invoices.reduce((s, inv) => s + (inv.taxAmount || 0), 0);
          if (invTotal > 0 && invVAT > 0) map[entry.linkId].caseInvoiceVAT += Math.ceil(invVAT * amt / invTotal);
        }
        map[entry.linkId].count += 1;
        map[entry.linkId].cases.push({
          id: req.id,
          allocated: amt,
          nameAr: req.nameArabic || req.applicantName || '',
          nameEn: req.nameEnglish || req.applicantName || '',
          idNumber: req.idNumber || req.nationalId || '',
          caseNumber: req.caseNumber,
        });
      }
    }
    console.log(`[perf] linkUsageMap: ${requests.length} requests × ${links.length} links in ${(performance.now() - t0).toFixed(1)}ms`);
    return map;
  }, [requests, links, donationSumByLink]);

  // Derive allLinks from links + linkUsageMap (for link picker). _key = linkId.
  const allLinks = useMemo(() => {
    const t0 = performance.now();
    const out = links.map(l => {
      const usage = linkUsageMap[l.id] || { actualReceived: donationSumByLink[l.id] || 0, totalAllocated: 0, caseInvoiceVAT: 0 };
      const ms = usage.actualReceived * marketingX / ((marketingX + marketingY) || 1);
      const marketingShare = Math.ceil(ms);
      const refundableVAT = Math.ceil(ms * 0.15) + (usage.caseInvoiceVAT || 0);
      // VAT on the marketing share is refundable, so it doesn't reduce the
      // amount available to the cases — only the marketing share itself does.
      const availableForCases = Math.floor(usage.actualReceived - marketingShare);
      return {
        _key: l.id,
        platform: l.platform,
        platformCaseNumber: l.caseNumber,
        actualReceived: usage.actualReceived,
        marketingShare,
        refundableVAT,
        availableForCases,
        totalAllocated: usage.totalAllocated,
        remaining: availableForCases - usage.totalAllocated,
        url: l.url,
        treatmentCategoryId: l.treatmentCategoryId,
        linkTitle: l.linkTitle,
        cases: (usage as any).cases || [],
      };
    });
    console.log(`[perf] allLinks: ${links.length} links in ${(performance.now() - t0).toFixed(1)}ms`);
    return out;
  }, [links, linkUsageMap, marketingX, marketingY, donationSumByLink]);

  // Per-project donation transactions manager (read-only for coordinator)
  const [managingDonationsLink, setManagingDonationsLink] = useState<typeof allLinks[0] | null>(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sort state
  const [sortColumn, setSortColumn] = useState<string>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Selected request
  const [selectedRequest, setSelectedRequest] = useState<TreatmentSupportRequest | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [idPreviewUrl, setIdPreviewUrl] = useState<string | null>(null);
  const [idPreviewRotation, setIdPreviewRotation] = useState(0);
  const [isSavingRotation, setIsSavingRotation] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [cropRect, setCropRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [cropDragStart, setCropDragStart] = useState<{ x: number; y: number } | null>(null);
  const [cropDragHandle, setCropDragHandle] = useState<string | null>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const [editingFileIndex, setEditingFileIndex] = useState<number | null>(null);
  const [editingFileType, setEditingFileType] = useState<'report' | 'invoice' | 'progressReport' | null>(null);
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [editingInvoiceData, setEditingInvoiceData] = useState({
    providerId: '',
    providerName: '',
    vatNumber: '',
    invoiceDate: '',
    invoiceNumber: '',
    totalBeforeDiscount: '',
    totalAfterDiscount: '',
    taxableAmount: '',
    nonTaxableAmount: '',
    taxAmount: '',
  });
  const [isEditingReport, setIsEditingReport] = useState(false);
  const [editingReportData, setEditingReportData] = useState({
    providerId: '',
    department: '',
    reportDate: '',
  });
  const [isEditingProgressReport, setIsEditingProgressReport] = useState(false);
  const [editingProgressReportData, setEditingProgressReportData] = useState({
    providerId: '',
    department: '',
    reportDate: '',
    notes: '',
  });
  
  const isPdfUrl = (url: string | undefined) => !!url && url.toLowerCase().includes('.pdf');
  const isImageUrl = (url: string | undefined) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'].some(ext => lower.includes(ext)) && !lower.includes('.pdf');
  };

  const handleSaveRotation = async () => {
    if (!idPreviewUrl || !selectedRequest || idPreviewRotation === 0) return;
    setIsSavingRotation(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = idPreviewUrl;
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const isVertical = idPreviewRotation === 90 || idPreviewRotation === 270;
      canvas.width = isVertical ? img.height : img.width;
      canvas.height = isVertical ? img.width : img.height;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((idPreviewRotation * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png');
      });

      const timestamp = Date.now();
      const file = new File([blob], `rotated_${timestamp}.png`, { type: 'image/png' });
      const url = await uploadFile(file, `id-documents/${selectedRequest.id}/${timestamp}_rotated.png`);

      await updateRequest(selectedRequest.id, {
        idDocumentUrl: url,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, idDocumentUrl: url } : null);
      setIdPreviewUrl(url);
      setIdPreviewRotation(0);
      toast.success(t('common.rotationSaved'));
    } catch {
      toast.error(t('common.rotationSaveError'));
    } finally {
      setIsSavingRotation(false);
    }
  };

  const handleSaveCrop = async () => {
    if (!idPreviewUrl || !selectedRequest || !cropRect) return;
    setIsSavingRotation(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = idPreviewUrl;
      });

      const sx = (cropRect.x / 100) * img.width;
      const sy = (cropRect.y / 100) * img.height;
      const sw = (cropRect.w / 100) * img.width;
      const sh = (cropRect.h / 100) * img.height;

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sw));
      canvas.height = Math.max(1, Math.round(sh));
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png');
      });

      const timestamp = Date.now();
      const file = new File([blob], `cropped_${timestamp}.png`, { type: 'image/png' });
      const url = await uploadFile(file, `id-documents/${selectedRequest.id}/${timestamp}_cropped.png`);

      await updateRequest(selectedRequest.id, {
        idDocumentUrl: url,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, idDocumentUrl: url } : null);
      setIdPreviewUrl(url);
      setCropRect(null);
      setIsCropping(false);
      toast.success(t('common.cropSaved'));
    } catch {
      toast.error(t('common.cropSaveError'));
    } finally {
      setIsSavingRotation(false);
    }
  };

  const getCropRelativePos = (clientX: number, clientY: number) => {
    const container = cropContainerRef.current;
    if (!container) return { x: 0, y: 0 };
    const rect = container.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    };
  };

  const onCropPointerDown = (e: React.PointerEvent) => {
    if (!isCropping) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const pos = getCropRelativePos(e.clientX, e.clientY);
    setCropDragStart(pos);
    setCropDragHandle(null);
    setCropRect(null);
  };

  const onCropPointerMove = (e: React.PointerEvent) => {
    if (!isCropping || !cropDragStart) return;
    e.preventDefault();
    e.stopPropagation();
    const pos = getCropRelativePos(e.clientX, e.clientY);
    if (cropDragHandle && cropRect) {
      const newRect = { ...cropRect };
      if (cropDragHandle.includes('t')) { const b = cropRect.y + cropRect.h; newRect.y = Math.min(pos.y, b - 1); newRect.h = b - newRect.y; }
      if (cropDragHandle.includes('b')) { newRect.h = Math.max(1, pos.y - cropRect.y); }
      if (cropDragHandle.includes('l')) { const r = cropRect.x + cropRect.w; newRect.x = Math.min(pos.x, r - 1); newRect.w = r - newRect.x; }
      if (cropDragHandle.includes('r')) { newRect.w = Math.max(1, pos.x - cropRect.x); }
      newRect.x = Math.max(0, newRect.x); newRect.y = Math.max(0, newRect.y);
      newRect.w = Math.min(100 - newRect.x, newRect.w); newRect.h = Math.min(100 - newRect.y, newRect.h);
      setCropRect(newRect);
    } else {
      setCropRect({ x: Math.min(cropDragStart.x, pos.x), y: Math.min(cropDragStart.y, pos.y), w: Math.abs(pos.x - cropDragStart.x), h: Math.abs(pos.y - cropDragStart.y) });
    }
  };

  const onCropPointerUp = (e: React.PointerEvent) => {
    if (!isCropping) return;
    e.stopPropagation();
    setCropDragStart(null);
    setCropDragHandle(null);
    if (cropRect && (cropRect.w < 1 || cropRect.h < 1)) setCropRect(null);
  };

  const onCropHandleDown = (handle: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setCropDragStart(getCropRelativePos(e.clientX, e.clientY));
    setCropDragHandle(handle);
  };

  const getCropHandleStyle = (handle: string, c: { x: number; y: number; w: number; h: number }): React.CSSProperties => {
    const s = 12, h2 = s / 2;
    const base: React.CSSProperties = { position: 'absolute', width: s, height: s };
    const positions: Record<string, React.CSSProperties> = {
      tl: { left: `${c.x}%`, top: `${c.y}%`, cursor: 'nwse-resize' },
      tr: { left: `${c.x + c.w}%`, top: `${c.y}%`, cursor: 'nesw-resize' },
      bl: { left: `${c.x}%`, top: `${c.y + c.h}%`, cursor: 'nesw-resize' },
      br: { left: `${c.x + c.w}%`, top: `${c.y + c.h}%`, cursor: 'nwse-resize' },
      t: { left: `${c.x + c.w / 2}%`, top: `${c.y}%`, cursor: 'ns-resize' },
      b: { left: `${c.x + c.w / 2}%`, top: `${c.y + c.h}%`, cursor: 'ns-resize' },
      l: { left: `${c.x}%`, top: `${c.y + c.h / 2}%`, cursor: 'ew-resize' },
      r: { left: `${c.x + c.w}%`, top: `${c.y + c.h / 2}%`, cursor: 'ew-resize' },
    };
    return { ...base, ...positions[handle], transform: `translate(-${h2}px, -${h2}px)` };
  };

  const getCategoryColor = (categoryId: string | undefined) => {
    if (!categoryId) return 'bg-gray-100 text-gray-800';
    const idx = activeCategories.findIndex(c => c.id === categoryId);
    return CATEGORY_COLORS[idx >= 0 ? idx % CATEGORY_COLORS.length : 0];
  };

  const getCategoryLabel = (request: TreatmentSupportRequest) => {
    if (request.categoryId) {
      const cat = activeCategories.find(c => c.id === request.categoryId);
      return dir === 'rtl'
        ? (cat?.nameAr || cat?.name || request.categoryName || '-')
        : (cat?.name || request.categoryName || '-');
    }
    return request.categoryName || '-';
  };

  const updateRequestCategory = async (request: TreatmentSupportRequest, newId: string | undefined) => {
    if (newId === request.categoryId) return;
    const conflicting = (request.donationEntries || []).filter(e => {
      const l = allLinks.find(x => x._key === e.linkId);
      return l && l.treatmentCategoryId && newId && l.treatmentCategoryId !== newId;
    });
    if (conflicting.length > 0) {
      const ok = window.confirm(t('dashboard.admin.helps.linksView.cascadeConfirm'));
      if (!ok) return;
    }
    const remainingEntries = (request.donationEntries || []).filter(e => !conflicting.includes(e));
    try {
      const cat = newId ? activeCategories.find(c => c.id === newId) : null;
      await updateRequest(request.id, {
        categoryId: newId,
        categoryName: cat ? (dir === 'rtl' ? (cat.nameAr || cat.name) : cat.name) : '',
        donationEntries: remainingEntries,
        updatedAt: new Date(),
      } as any);
    } catch (err) {
      console.error('Error updating case category:', err);
      toast.error(t('common.saveError'));
    }
  };

  // Sync selectedRequest with requests array when data is refreshed
  useEffect(() => {
    if (selectedRequest && requests.length > 0) {
      const updated = requests.find(r => r.id === selectedRequest.id);
      if (updated) {
        setSelectedRequest(updated);
      }
    }
  }, [requests]);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editFormData, setEditFormData] = useState({
    nameArabic: '',
    nameEnglish: '',
    idNumber: '',
    idType: 'national_id' as TreatmentIdType,
    dateOfBirth: '',
    idExpiryDate: '',
    nationality: '',
    gender: 'male' as TreatmentGender,
    maritalStatus: 'single' as TreatmentMaritalStatus,
    familyMembersCount: '',
    email: '',
    educationLevel: '' as TreatmentEducationLevel | '',
    workSector: '' as TreatmentWorkSector | '',
    profession: '',
    monthlyIncome: '',
    housingType: '' as TreatmentHousingType | '',
    rentAmount: '',
    city: '',
    neighborhood: '',
    phoneNumber: '',
    relativeMobileNumber: '',
    isCritical: false,
    isHospitalized: false,
    hasInsurance: false,
    insuranceProvider: '',
    insuranceCategory: '',
    categoryId: '',
    medicalCondition: '',
    requiredTreatment: '',
    cost: '',
    coordinatorNotes: '',
  });
  
  // Donation entries state
  const [showAllocateLink, setShowAllocateLink] = useState(false);
  const [openAllocateLinkPicker, setOpenAllocateLinkPicker] = useState(false);
  const [allocateLinkKey, setAllocateLinkKey] = useState('');
  const [allocateAmount, setAllocateAmount] = useState('');
  const [pendingAllocations, setPendingAllocations] = useState<{ linkKey: string; amount: number }[]>([]);
  const [editingDonationIndex, setEditingDonationIndex] = useState<number | null>(null);
  const [editingDonation, setEditingDonation] = useState({
    linkId: '',
    platform: '' as '' | 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
    caseNumber: '',
    amount: '',
  });

  // Update state
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [newUpdateText, setNewUpdateText] = useState('');
  
  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Applicant information
    nameArabic: '',
    nameEnglish: '',
    idNumber: '',
    idType: 'national_id' as TreatmentIdType,
    dateOfBirth: '',
    idExpiryDate: '',
    nationality: '',
    gender: 'male' as TreatmentGender,
    maritalStatus: 'single' as TreatmentMaritalStatus,
    familyMembersCount: '',
    email: '',
    educationLevel: '' as TreatmentEducationLevel | '',
    workSector: '' as TreatmentWorkSector | '',
    profession: '',
    monthlyIncome: '',
    housingType: '' as TreatmentHousingType | '',
    rentAmount: '',
    city: '',
    neighborhood: '',
    phoneNumber: '',
    relativeMobileNumber: '',
    // Case status
    isCritical: false,
    isHospitalized: false,
    hasInsurance: false,
    insuranceProvider: '',
    insuranceCategory: '',
    categoryId: '',
    // Medical information
    medicalCondition: '',
    requiredTreatment: '',
    cost: '',
    coordinatorNotes: '',
    donationPlatform: '' as '' | 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
    donationCaseNumber: '',
  });

  const [pendingReports, setPendingReports] = useState<PendingReport[]>([]);
  const [pendingIdDocument, setPendingIdDocument] = useState<File | null>(null);

  // Review form
  const [reviewData, setReviewData] = useState({
    socialStatusFits: false,
    medicalConditionFits: false,
    coordinatorNotes: '',
  });

  // Invoice upload
  const [pendingInvoices, setPendingInvoices] = useState<PendingInvoice[]>([]);

  // Progress report upload
  const [pendingProgressReports, setPendingProgressReports] = useState<PendingProgressReport[]>([]);
  const [startPromptRequest, setStartPromptRequest] = useState<TreatmentSupportRequest | null>(null);

  const resetForm = () => {
    setFormData({
      nameArabic: '',
      nameEnglish: '',
      idNumber: '',
      idType: 'national_id',
      dateOfBirth: '',
      idExpiryDate: '',
      nationality: '',
      gender: 'male',
      maritalStatus: 'single',
      familyMembersCount: '',
      email: '',
      educationLevel: '',
      workSector: '',
      profession: '',
      monthlyIncome: '',
      housingType: '',
      rentAmount: '',
      city: '',
      neighborhood: '',
      phoneNumber: '',
      relativeMobileNumber: '',
      isCritical: false,
      isHospitalized: false,
      hasInsurance: false,
      insuranceProvider: '',
      insuranceCategory: '',
      categoryId: '',
      medicalCondition: '',
      requiredTreatment: '',
      cost: '',
      coordinatorNotes: '',
      donationPlatform: '',
      donationCaseNumber: '',
    });
    setPendingReports([]);
    setPendingIdDocument(null);
  };

  const uploadFile = async (file: File, path: string): Promise<string> => {
    // [storage-debug] surface auth state and bucket so we can diagnose
    // recurring storage/unauthorized failures.
    try {
      const { getAuth } = await import('firebase/auth');
      const a = getAuth();
      console.log('[storage-debug] upload', {
        path,
        size: file.size,
        type: file.type,
        bucket: storage.app.options.storageBucket,
        currentUserUid: a.currentUser?.uid || null,
        hasToken: !!(await a.currentUser?.getIdToken?.()),
      });
    } catch {}
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  // Add new case manually
  const handleAddCase = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation - only phone and ID number are required
    if (!formData.idNumber || !formData.phoneNumber) {
      toast.error(t('dashboard.coordinator.validation.required'));
      return;
    }

    // Validate ID format based on type
    if (formData.idType === 'national_id' && !/^[12]\d{9}$/.test(formData.idNumber)) {
      toast.error(t('dashboard.coordinator.validation.nationalId'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload all pending reports
      const uploadedReports = [];
      for (const report of pendingReports) {
        const timestamp = Date.now();
        const url = await uploadFile(
          report.file,
          `treatment-reports/coordinator/${timestamp}_${report.file.name}`
        );
        uploadedReports.push({
          url,
          name: report.file.name,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
            reportDate: report.reportDate ? new Date(report.reportDate) : new Date(),
          providerId: report.providerId,
          providerName: report.providerName,
          department: report.department,
        });
      }
      const medicalReportUrl = uploadedReports.length > 0 ? uploadedReports[0].url : '';

      // Upload ID document if provided
      let idDocumentUrl = '';
      let idDocumentName = '';
      if (pendingIdDocument) {
        const timestamp = Date.now();
        idDocumentUrl = await uploadFile(
          pendingIdDocument,
          `id-documents/coordinator/${timestamp}_${pendingIdDocument.name}`
        );
        idDocumentName = pendingIdDocument.name;
      }

      const requestData: Parameters<typeof addRequest>[0] = {
        userId: `coordinator_${currentUser?.uid}`,
        // New applicant fields
        nameArabic: formData.nameArabic,
        nameEnglish: formData.nameEnglish,
        idNumber: formData.idNumber,
        idType: formData.idType,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined as any,
        idExpiryDate: formData.idExpiryDate ? new Date(formData.idExpiryDate) : undefined as any,
        nationality: formData.nationality,
        gender: formData.gender,
        maritalStatus: formData.maritalStatus,
        familyMembersCount: Number(formData.familyMembersCount),
        email: formData.email || undefined,
        educationLevel: formData.educationLevel || undefined,
        workSector: formData.workSector || undefined,
        profession: formData.profession || undefined,
        monthlyIncome: formData.monthlyIncome ? Number(formData.monthlyIncome) : undefined,
        housingType: formData.housingType || undefined,
        rentAmount: formData.housingType === 'rented' && formData.rentAmount ? Number(formData.rentAmount) : undefined,
        city: formData.city || undefined,
        neighborhood: formData.neighborhood || undefined,
        relativeMobileNumber: formData.relativeMobileNumber,
        // Case status
        isCritical: formData.isCritical,
        isHospitalized: formData.isHospitalized,
        hasInsurance: formData.hasInsurance,
        insuranceProvider: formData.hasInsurance ? formData.insuranceProvider : '',
        insuranceCategory: formData.hasInsurance ? formData.insuranceCategory : '',
        categoryId: formData.categoryId || undefined,
        categoryName: formData.categoryId ? activeCategories.find(c => c.id === formData.categoryId)?.name : undefined,
        // Medical information
        medicalCondition: formData.medicalCondition,
        requiredTreatment: formData.requiredTreatment,
        cost: Number(formData.cost),
        medicalReportUrl,
        medicalReports: uploadedReports,
        status: 'pending',
        coordinatorNotes: formData.coordinatorNotes,
        createdAt: new Date(),
        createdBy: 'coordinator',
      };
      if (formData.phoneNumber) requestData.phoneNumber = formData.phoneNumber;
      if (idDocumentUrl) {
        requestData.idDocumentUrl = idDocumentUrl;
        requestData.idDocumentName = idDocumentName;
        requestData.idDocumentUploadedAt = new Date();
      }
      
      await addRequest(requestData);
      
      toast.success(t('dashboard.coordinator.addSuccess'));
      setIsAddOpen(false);
      resetForm();
    } catch (error: any) {
      console.error('Error adding case:', error);
      const detail = error?.code || error?.message || String(error);
      toast.error(`${t('dashboard.coordinator.addError')}: ${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Review a case
  const handleReviewCase = async () => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      const fitsAllCriteria = reviewData.socialStatusFits && reviewData.medicalConditionFits;
      
      // Check if auto-approve is enabled
      let newStatus: TreatmentSupportStatus = 'rejected';
      if (fitsAllCriteria) {
        const settingsDoc = await getDoc(doc(db, 'settings', 'treatmentAutoApprove'));
        const autoApproveEnabled = settingsDoc.exists() && settingsDoc.data().enabled === true;
        newStatus = autoApproveEnabled ? 'approved' : 'pending_approval';
      }
      
      await updateRequest(selectedRequest.id, {
        status: newStatus,
        coordinatorId: currentUser?.uid,
        coordinatorNotes: reviewData.coordinatorNotes,
        socialStatusFits: reviewData.socialStatusFits,
        medicalConditionFits: reviewData.medicalConditionFits,
        reviewedAt: new Date(),
        updatedAt: new Date(),
      });
      
      toast.success(fitsAllCriteria 
        ? t('dashboard.coordinator.sentForApproval')
        : t('dashboard.coordinator.caseRejected')
      );
      setIsReviewOpen(false);
      setSelectedRequest(null);
    } catch (error) {
      toast.error(t('dashboard.coordinator.reviewError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Upload invoices
  const handleUploadInvoices = async () => {
    if (!selectedRequest || pendingInvoices.length === 0) return;
    
    setIsSubmitting(true);
    try {
      const invoiceUrls: string[] = [];
      const uploadedInvoices = [];
      
      for (const invoice of pendingInvoices) {
        const timestamp = Date.now();
        // file is optional — for numbers-only entries we skip the upload
        // and store empty url/name placeholders.
        const url = invoice.file
          ? await uploadFile(
              invoice.file,
              `treatment-invoices/${selectedRequest.id}/${timestamp}_${invoice.file.name}`,
            )
          : '';
        invoiceUrls.push(url);
        uploadedInvoices.push({
          url,
          name: invoice.file?.name || `Invoice ${invoice.invoiceNumber || timestamp}`,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
          providerId: invoice.providerId,
          providerName: invoice.providerName,
          providerNameAr: invoice.providerNameAr,
          vatNumber: invoice.vatNumber,
          invoiceDate: new Date(invoice.invoiceDate),
          invoiceNumber: invoice.invoiceNumber,
          totalBeforeDiscount: invoice.totalBeforeDiscount,
          totalAfterDiscount: invoice.totalAfterDiscount,
          taxableAmount: invoice.taxableAmount,
          nonTaxableAmount: invoice.nonTaxableAmount,
          taxAmount: invoice.taxAmount,
        });
      }

      await updateRequest(selectedRequest.id, {
        status: 'pending_invoices',
        invoiceUrls,
        invoices: uploadedInvoices,
        invoicesUploadedAt: new Date(),
        invoicesUploadedBy: currentUser?.uid,
        updatedAt: new Date(),
      });
      
      toast.success(t('dashboard.coordinator.invoicesUploaded'));
      setIsInvoiceOpen(false);
      setSelectedRequest(null);
      setPendingInvoices([]);
    } catch (error: any) {
      console.error('Upload invoices error:', error);
      const detail = error?.code || error?.message || String(error);
      toast.error(`${t('dashboard.coordinator.invoiceError')}: ${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Upload progress reports
  const handleUploadProgressReports = async () => {
    if (!selectedRequest || pendingProgressReports.length === 0) return;
    try {
      const timestamp = Date.now();
      const uploaded = await Promise.all(pendingProgressReports.map(async (p, i) => {
        if (!p.file) throw new Error('missing file');
        const path = `treatment-progress-reports/${selectedRequest.id}/${timestamp}_${i}_${p.file.name}`;
        const url = await uploadFile(p.file, path);
        return {
          url,
          name: p.file.name,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
          reportDate: p.reportDate ? new Date(p.reportDate) : undefined,
          providerId: p.providerId,
          providerName: p.providerName,
          department: p.department || undefined,
          notes: p.notes || undefined,
        };
      }));
      const previousCount = (selectedRequest.progressReports || []).length;
      const next = [...(selectedRequest.progressReports || []), ...uploaded];
      await updateRequest(selectedRequest.id, { progressReports: next, updatedAt: new Date() });
      setSelectedRequest(prev => prev ? { ...prev, progressReports: next } : null);
      setPendingProgressReports([]);
      toast.success(t('common.progressReports.toast.uploadSuccess'));
      if (shouldOfferStartTreatment(selectedRequest.status, previousCount, next.length)) {
        setStartPromptRequest({ ...selectedRequest, progressReports: next });
      }
    } catch (e) {
      console.error(e);
      toast.error(t('common.progressReports.toast.uploadFailed'));
    }
  };

  const handleUpdateProgressReportMetadata = async () => {
    if (!selectedRequest || editingFileIndex === null) return;

    const existingReports = selectedRequest.progressReports || [];
    if (editingFileIndex >= existingReports.length) return;

    setIsSubmitting(true);
    try {
      const updatedReports = [...existingReports];
      const editProvider = activeProviders.find(p => p.id === editingProgressReportData.providerId);
      updatedReports[editingFileIndex] = {
        ...updatedReports[editingFileIndex],
        providerId: editingProgressReportData.providerId,
        providerName: editProvider?.name || updatedReports[editingFileIndex].providerName || '',
        department: editingProgressReportData.department || undefined,
        reportDate: editingProgressReportData.reportDate ? new Date(editingProgressReportData.reportDate) : updatedReports[editingFileIndex].reportDate,
        notes: editingProgressReportData.notes || undefined,
      };

      await updateRequest(selectedRequest.id, {
        progressReports: updatedReports,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, progressReports: updatedReports } : null);
      setIsEditingProgressReport(false);
      toast.success(t('dashboard.coordinator.reportUpdated'));
    } catch (error) {
      toast.error(t('dashboard.coordinator.reportUpdateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProgressReport = async (reportIndex: number) => {
    if (!selectedRequest) return;
    const next = (selectedRequest.progressReports || []).filter((_, i) => i !== reportIndex);
    try {
      await updateRequest(selectedRequest.id, { progressReports: next, updatedAt: new Date() });
      setSelectedRequest(prev => prev ? { ...prev, progressReports: next } : null);
      toast.success(t('common.progressReports.toast.deleteSuccess'));
    } catch {
      toast.error(t('common.progressReports.toast.deleteFailed'));
    }
  };

  // Move approved_treatment -> treatment_started (requires >=1 progress report)
  const handleStartTreatment = async (request: TreatmentSupportRequest) => {
    const gate = canStartTreatment(request);
    if (!gate.ok) {
      toast.error(t('dashboard.coordinator.startTreatmentGateError'));
      return;
    }
    try {
      const now = new Date();
      const newUpdate = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        content: t('dashboard.coordinator.timeline.treatmentStartedContent'),
        statusAtTime: 'treatment_started' as const,
        createdAt: now,
        createdBy: currentUser?.uid || '',
        createdByName: currentUser?.displayName || t('common.unknownUser'),
        createdByRole: 'coordinator' as const,
      };
      await updateRequest(request.id, {
        status: 'treatment_started',
        treatmentStartedAt: now,
        treatmentStartedBy: currentUser?.uid,
        updatedAt: now,
        updates: [...(request.updates || []), newUpdate],
      });
      setSelectedRequest(prev => prev && prev.id === request.id ? {
        ...prev,
        status: 'treatment_started',
        treatmentStartedAt: now,
        treatmentStartedBy: currentUser?.uid,
        updates: [...(request.updates || []), newUpdate],
      } : prev);
      toast.success(t('dashboard.coordinator.startTreatmentSuccess'));
    } catch (error) {
      console.error(error);
      toast.error(t('dashboard.coordinator.startTreatmentError'));
    }
  };

  // Move treatment_started -> pending_invoices (label "Treatment Completed")
  const handleMarkCompleted = async (request: TreatmentSupportRequest) => {
    if (!(request.invoiceUrls?.length || request.invoices?.length)) {
      toast.error(t('dashboard.coordinator.noInvoicesToMarkTreated'));
      return;
    }
    try {
      const now = new Date();
      const newUpdate = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        content: t('dashboard.coordinator.timeline.treatmentCompletedContent'),
        statusAtTime: 'pending_invoices' as const,
        createdAt: now,
        createdBy: currentUser?.uid || '',
        createdByName: currentUser?.displayName || t('common.unknownUser'),
        createdByRole: 'coordinator' as const,
      };
      await updateRequest(request.id, {
        status: 'pending_invoices',
        updatedAt: now,
        updates: [...(request.updates || []), newUpdate],
      });
      toast.success(t('dashboard.coordinator.markTreatedSuccess'));
    } catch (error) {
      console.error(error);
      toast.error(t('dashboard.coordinator.markTreatedError'));
    }
  };

  // Start editing details
  const startEditingDetails = () => {
    if (!selectedRequest) return;
    setEditFormData({
      nameArabic: selectedRequest.nameArabic || '',
      nameEnglish: selectedRequest.nameEnglish || '',
      idNumber: selectedRequest.idNumber || selectedRequest.nationalId || '',
      idType: selectedRequest.idType || 'national_id',
      dateOfBirth: selectedRequest.dateOfBirth ? new Date(selectedRequest.dateOfBirth).toISOString().split('T')[0] : '',
      idExpiryDate: selectedRequest.idExpiryDate ? new Date(selectedRequest.idExpiryDate).toISOString().split('T')[0] : '',
      nationality: selectedRequest.nationality || '',
      gender: selectedRequest.gender || 'male',
      maritalStatus: selectedRequest.maritalStatus || 'single',
      familyMembersCount: String(selectedRequest.familyMembersCount || ''),
      email: selectedRequest.email || '',
      educationLevel: selectedRequest.educationLevel || '',
      workSector: selectedRequest.workSector || '',
      profession: selectedRequest.profession || '',
      monthlyIncome: selectedRequest.monthlyIncome != null ? String(selectedRequest.monthlyIncome) : '',
      housingType: selectedRequest.housingType || '',
      rentAmount: selectedRequest.rentAmount != null ? String(selectedRequest.rentAmount) : '',
      city: selectedRequest.city || '',
      neighborhood: selectedRequest.neighborhood || '',
      phoneNumber: selectedRequest.phoneNumber || '',
      relativeMobileNumber: selectedRequest.relativeMobileNumber || '',
      isCritical: selectedRequest.isCritical ?? false,
      isHospitalized: selectedRequest.isHospitalized ?? false,
      hasInsurance: selectedRequest.hasInsurance ?? false,
      insuranceProvider: selectedRequest.insuranceProvider || '',
      insuranceCategory: selectedRequest.insuranceCategory || '',
      categoryId: selectedRequest.categoryId || '',
      medicalCondition: selectedRequest.medicalCondition || '',
      requiredTreatment: selectedRequest.requiredTreatment || '',
      cost: String(selectedRequest.cost || ''),
      coordinatorNotes: selectedRequest.coordinatorNotes || '',
    });
    setIsEditingDetails(true);
  };

  // Save edited details
  const saveEditedDetails = async () => {
    if (!selectedRequest) return;
    
    if (!editFormData.idNumber || !editFormData.phoneNumber) {
      toast.error(t('dashboard.coordinator.validation.required'));
      return;
    }

    if (editFormData.idType === 'national_id' && !/^[12]\d{9}$/.test(editFormData.idNumber)) {
      toast.error(t('dashboard.coordinator.validation.nationalId'));
      return;
    }

    setIsSubmitting(true);
    try {
      await updateRequest(selectedRequest.id, {
        nameArabic: editFormData.nameArabic,
        nameEnglish: editFormData.nameEnglish,
        idNumber: editFormData.idNumber,
        idType: editFormData.idType,
        dateOfBirth: editFormData.dateOfBirth ? new Date(editFormData.dateOfBirth) : undefined,
        idExpiryDate: editFormData.idExpiryDate ? new Date(editFormData.idExpiryDate) : undefined,
        nationality: editFormData.nationality,
        gender: editFormData.gender,
        maritalStatus: editFormData.maritalStatus,
        familyMembersCount: Number(editFormData.familyMembersCount),
        email: editFormData.email || undefined,
        educationLevel: editFormData.educationLevel || undefined,
        workSector: editFormData.workSector || undefined,
        profession: editFormData.profession || undefined,
        monthlyIncome: editFormData.monthlyIncome ? Number(editFormData.monthlyIncome) : undefined,
        housingType: editFormData.housingType || undefined,
        rentAmount: editFormData.housingType === 'rented' && editFormData.rentAmount ? Number(editFormData.rentAmount) : undefined,
        city: editFormData.city || undefined,
        neighborhood: editFormData.neighborhood || undefined,
        phoneNumber: editFormData.phoneNumber,
        relativeMobileNumber: editFormData.relativeMobileNumber,
        medicalCondition: editFormData.medicalCondition,
        requiredTreatment: editFormData.requiredTreatment,
        cost: Number(editFormData.cost),
        isCritical: editFormData.isCritical,
        isHospitalized: editFormData.isHospitalized,
        hasInsurance: editFormData.hasInsurance,
        insuranceProvider: editFormData.insuranceProvider,
        insuranceCategory: editFormData.insuranceCategory,
        categoryId: editFormData.categoryId || '',
        categoryName: editFormData.categoryId
          ? (activeCategories.find(c => c.id === editFormData.categoryId)?.name || activeCategories.find(c => c.id === editFormData.categoryId)?.nameAr || '')
          : '',
        coordinatorNotes: editFormData.coordinatorNotes,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? {
        ...prev,
        nameArabic: editFormData.nameArabic,
        nameEnglish: editFormData.nameEnglish,
        idNumber: editFormData.idNumber,
        idType: editFormData.idType,
        dateOfBirth: editFormData.dateOfBirth ? new Date(editFormData.dateOfBirth) : prev.dateOfBirth,
        idExpiryDate: editFormData.idExpiryDate ? new Date(editFormData.idExpiryDate) : prev.idExpiryDate,
        nationality: editFormData.nationality,
        gender: editFormData.gender,
        maritalStatus: editFormData.maritalStatus,
        familyMembersCount: Number(editFormData.familyMembersCount),
        email: editFormData.email,
        educationLevel: editFormData.educationLevel || undefined,
        workSector: editFormData.workSector || undefined,
        profession: editFormData.profession,
        monthlyIncome: editFormData.monthlyIncome ? Number(editFormData.monthlyIncome) : undefined,
        housingType: editFormData.housingType || undefined,
        rentAmount: editFormData.rentAmount ? Number(editFormData.rentAmount) : undefined,
        city: editFormData.city,
        neighborhood: editFormData.neighborhood,
        phoneNumber: editFormData.phoneNumber,
        relativeMobileNumber: editFormData.relativeMobileNumber,
        medicalCondition: editFormData.medicalCondition,
        requiredTreatment: editFormData.requiredTreatment,
        cost: Number(editFormData.cost),
        isCritical: editFormData.isCritical,
        isHospitalized: editFormData.isHospitalized,
        hasInsurance: editFormData.hasInsurance,
        insuranceProvider: editFormData.insuranceProvider,
        insuranceCategory: editFormData.insuranceCategory,
        categoryId: editFormData.categoryId || '',
        categoryName: editFormData.categoryId
          ? (activeCategories.find(c => c.id === editFormData.categoryId)?.name || activeCategories.find(c => c.id === editFormData.categoryId)?.nameAr || '')
          : (prev.categoryName || ''),
        coordinatorNotes: editFormData.coordinatorNotes,
      } : null);
      
      toast.success(t('dashboard.coordinator.editSuccess'));
      setIsEditingDetails(false);
    } catch (error) {
      toast.error(t('dashboard.coordinator.editError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get remaining budget for a link (after marketing deduction)
  const getLinkRemainingBudget = (linkId: string, excludeCaseId?: string, excludeEntryIdx?: number) => {
    const usage = linkUsageMap[linkId];
    if (!usage) return { exists: false, actualReceived: 0, availableForCases: 0, totalAllocated: 0, remaining: Infinity, count: 0 };
    let totalAllocated = usage.totalAllocated;
    if (excludeCaseId !== undefined && excludeEntryIdx !== undefined) {
      const req = requests.find(r => r.id === excludeCaseId);
      const entry = req?.donationEntries?.[excludeEntryIdx];
      if (entry) totalAllocated -= (entry.amount || 0);
    }
    const available = calcLinkAvailable(usage.actualReceived);
    const remaining = available - totalAllocated;
    return { exists: true, actualReceived: usage.actualReceived, availableForCases: available, totalAllocated, remaining, count: usage.count };
  };

  // Get remaining budget for a case
  const getCaseRemainingBudget = (caseId: string, excludeLinkKey?: string) => {
    const req = requests.find(r => r.id === caseId);
    if (!req) return { caseCost: 0, totalFunded: 0, remaining: 0, hasVAT: false };
    const invoicesTotal = req.invoices && req.invoices.length > 0
      ? req.invoices.reduce((sum, inv) => sum + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0)
      : 0;
    const hasInvoices = invoicesTotal > 0;
    const invoiceVAT = hasInvoices
      ? req.invoices!.reduce((sum, inv) => sum + (inv.taxAmount || 0), 0)
      : 0;
    // NET medical cost: invoice totals if any, else declared case.cost.
    // entry.amount is NET (sourced from the link's post-marketing pool),
    // so we compare NET to NET. getEffectiveCost (GROSS) stays available
    // for the fundraising-target view; not used as case-side denominator.
    const base = hasInvoices ? invoicesTotal : (req.cost || 0);
    const caseCost = base;
    const hasVAT = hasInvoices ? invoiceVAT > 0 : false;
    let totalFunded = 0;
    for (const entry of (req.donationEntries || [])) {
      if (excludeLinkKey && entry.linkId === excludeLinkKey) continue;
      totalFunded += entry.amount || 0;
    }
    return { caseCost, totalFunded, remaining: caseCost - totalFunded, hasVAT };
  };

  // Queue a single allocation into the pending list (validated)
  const addToPendingAllocations = (): boolean => {
    if (!selectedRequest || !allocateLinkKey) return false;
    const link = allLinks.find(l => l._key === allocateLinkKey);
    if (!link) return false;

    const alreadyOnThisCase = selectedRequest.donationEntries?.some(
      e => e.linkId === link._key
    );
    if (alreadyOnThisCase || pendingAllocations.some(p => p.linkKey === allocateLinkKey)) {
      toast.error(t('dashboard.coordinator.linksView?.duplicateLink') || 'Link already on this case');
      return false;
    }

    const entryAmount = Number(allocateAmount) || 0;
    if (entryAmount <= 0) return false;

    const linkBudget = getLinkRemainingBudget(link._key);
    if (entryAmount > linkBudget.remaining) {
      toast.error(t('dashboard.coordinator.linkBudget.exceedsRemaining', { remaining: linkBudget.remaining.toLocaleString('en-US') }));
      return false;
    }
    const caseBudget = getCaseRemainingBudget(selectedRequest.id);
    const queuedSum = pendingAllocations.reduce((s, p) => s + p.amount, 0);
    if (caseBudget.caseCost > 0 && (entryAmount + queuedSum) > caseBudget.remaining) {
      toast.error(t('dashboard.coordinator.caseBudget.exceedsRemaining', { remaining: (caseBudget.remaining - queuedSum).toLocaleString('en-US') }));
      return false;
    }

    try {
      assertCategoryMatch(link, selectedRequest);
    } catch (e: any) {
      toast.error(t(e.code === 'CATEGORY_MISSING'
        ? 'dashboard.admin.helps.alloc.categoryMissing'
        : 'dashboard.admin.helps.alloc.categoryMismatch'));
      return false;
    }

    setPendingAllocations(prev => [...prev, { linkKey: allocateLinkKey, amount: entryAmount }]);
    setAllocateLinkKey('');
    setAllocateAmount('');
    return true;
  };

  // Commit all queued allocations (plus current selection if filled)
  const handleAllocateLink = async () => {
    if (!selectedRequest) return;

    // Build the final list — include current picker selection if it has a valid amount
    let allocations = [...pendingAllocations];
    if (allocateLinkKey && Number(allocateAmount) > 0) {
      if (addToPendingAllocations()) {
        allocations = [...pendingAllocations, { linkKey: allocateLinkKey, amount: Number(allocateAmount) }];
      } else {
        return;
      }
    }
    if (allocations.length === 0) return;

    setIsSubmitting(true);
    try {
      const existingEntries = selectedRequest.donationEntries || [];
      const newEntries: DonationEntry[] = [];
      for (const alloc of allocations) {
        const link = allLinks.find(l => l._key === alloc.linkKey);
        if (!link) continue;
        try {
          assertCategoryMatch(link, selectedRequest);
        } catch (e: any) {
          toast.error(t(e.code === 'CATEGORY_MISSING'
            ? 'dashboard.admin.helps.alloc.categoryMissing'
            : 'dashboard.admin.helps.alloc.categoryMismatch'));
          continue;
        }
        const newEntry: DonationEntry = {
          linkId: link._key,
          platform: link.platform,
          caseNumber: link.platformCaseNumber,
          amount: alloc.amount,
          addedAt: new Date(),
          addedBy: currentUser?.uid,
        };
        newEntries.push(newEntry);
      }

      const updatedEntries = [...existingEntries, ...newEntries];

      await updateRequest(selectedRequest.id, {
        donationEntries: updatedEntries,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, donationEntries: updatedEntries } : null);
      setAllocateLinkKey('');
      setAllocateAmount('');
      setPendingAllocations([]);
      setShowAllocateLink(false);
      toast.success(t('dashboard.coordinator.donationAdded'));
    } catch (error) {
      toast.error(t('dashboard.coordinator.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove donation entry
  const handleRemoveDonation = async (index: number) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      const existingEntries = selectedRequest.donationEntries || [];
      const updatedEntries = existingEntries.filter((_, i) => i !== index);
      
      await updateRequest(selectedRequest.id, {
        donationEntries: updatedEntries,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? { ...prev, donationEntries: updatedEntries } : null);
      toast.success(t('dashboard.coordinator.donationRemoved'));
    } catch (error) {
      toast.error(t('dashboard.coordinator.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Start editing a donation entry
  const startEditingDonation = (index: number) => {
    const entry = selectedRequest?.donationEntries?.[index];
    if (entry) {
      setEditingDonationIndex(index);
      setEditingDonation({
        linkId: entry.linkId,
        platform: entry.platform,
        caseNumber: entry.caseNumber,
        amount: entry.amount?.toString() || '',
      });
    }
  };

  // Save edited donation entry
  const saveEditedDonation = async () => {
    if (!selectedRequest || editingDonationIndex === null) return;

    // Dual budget validation
    const editAmount = Number(editingDonation.amount) || 0;
    const budget = getLinkRemainingBudget(editingDonation.linkId, selectedRequest.id, editingDonationIndex);
    if (budget.exists && budget.actualReceived > 0) {
      if (editAmount > budget.remaining) {
        toast.error(t('dashboard.coordinator.linkBudget.exceedsRemaining', { remaining: budget.remaining.toLocaleString('en-US') }));
        return;
      }
    }
    const caseBudgetEdit = getCaseRemainingBudget(selectedRequest.id, editingDonation.linkId);
    if (caseBudgetEdit.caseCost > 0 && editAmount > caseBudgetEdit.remaining) {
      toast.error(t('dashboard.coordinator.caseBudget.exceedsRemaining', { remaining: caseBudgetEdit.remaining.toLocaleString('en-US') }));
      return;
    }

    const linkForGate = allLinks.find(l => l._key === editingDonation.linkId);
    if (linkForGate && selectedRequest) {
      try {
        assertCategoryMatch(linkForGate, selectedRequest);
      } catch (e: any) {
        toast.error(t(e.code === 'CATEGORY_MISSING'
          ? 'dashboard.admin.helps.alloc.categoryMissing'
          : 'dashboard.admin.helps.alloc.categoryMismatch'));
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const existingEntries = selectedRequest.donationEntries || [];
      const updatedEntries = existingEntries.map((entry, i) => {
        if (i === editingDonationIndex) {
          const updatedEntry: DonationEntry = {
            linkId: editingDonation.linkId,
            platform: editingDonation.platform as 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
            caseNumber: editingDonation.caseNumber,
            amount: editAmount,
            addedAt: entry.addedAt,
            addedBy: entry.addedBy,
          };
          return updatedEntry;
        }
        return entry;
      });
      
      await updateRequest(selectedRequest.id, {
        donationEntries: updatedEntries,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? { ...prev, donationEntries: updatedEntries } : null);
      setEditingDonationIndex(null);
      toast.success(t('dashboard.coordinator.donationUpdated'));
    } catch (error) {
      toast.error(t('dashboard.coordinator.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add case update
  const handleAddUpdate = async () => {
    if (!selectedRequest || !newUpdateText.trim()) return;
    
    setIsSubmitting(true);
    try {
      const newUpdate: CaseUpdate = {
        id: `update_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        content: newUpdateText.trim(),
        statusAtTime: selectedRequest.status,
        createdAt: new Date(),
        createdBy: currentUser?.uid || '',
        createdByName: currentUser?.displayName || currentUser?.email || 'Unknown',
        createdByRole: 'coordinator',
      };
      
      const existingUpdates = selectedRequest.updates || [];
      const updatedUpdates = [...existingUpdates, newUpdate];
      
      await updateRequest(selectedRequest.id, {
        updates: updatedUpdates,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? { ...prev, updates: updatedUpdates } : null);
      setNewUpdateText('');
      setShowAddUpdate(false);
      toast.success(t('dashboard.coordinator.updates.addUpdate'));
    } catch (error) {
      toast.error(t('dashboard.coordinator.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Upload ID document
  const handleUploadIdDocument = async (file: File) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      const timestamp = Date.now();
      const url = await uploadFile(
        file,
        `id-documents/${selectedRequest.id}/${timestamp}_${file.name}`
      );
      
      await updateRequest(selectedRequest.id, {
        idDocumentUrl: url,
        idDocumentName: file.name,
        idDocumentUploadedAt: new Date(),
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? {
        ...prev,
        idDocumentUrl: url,
        idDocumentName: file.name,
      } : null);
      
      toast.success(t('dashboard.coordinator.idDocumentUploaded'));
    } catch (error) {
      console.error('ID document upload error:', error);
      toast.error(t('dashboard.coordinator.idDocumentError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add report from details view
  const handleAddReportFromDetails = async () => {
    if (!selectedRequest || pendingReports.length === 0) return;
    
    setIsSubmitting(true);
    try {
      const existingReports = selectedRequest.medicalReports || [];
      if (selectedRequest.medicalReportUrl && existingReports.length === 0) {
        existingReports.push({
          url: selectedRequest.medicalReportUrl,
          name: 'Medical Report',
          uploadedAt: selectedRequest.createdAt,
        });
      }
      
      const newReports = [...existingReports];
      
      for (const report of pendingReports) {
        const timestamp = Date.now();
        const reportUrl = await uploadFile(
          report.file,
          `treatment-reports/${selectedRequest.id}/${timestamp}_${report.file.name}`
        );
        
        newReports.push({
          url: reportUrl,
          name: report.file.name,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
          providerId: report.providerId,
          providerName: report.providerName,
          department: report.department,
          reportDate: report.reportDate ? new Date(report.reportDate) : undefined,
        });
      }
      
      await updateRequest(selectedRequest.id, {
        medicalReports: newReports,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? {
        ...prev,
        medicalReports: newReports,
      } : null);
      
      toast.success(t('dashboard.coordinator.reportsAdded'));
      setPendingReports([]);
    } catch (error) {
      toast.error(t('dashboard.coordinator.reportsError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add invoice from details view
  const handleAddInvoiceFromDetails = async () => {
    if (!selectedRequest || pendingInvoices.length === 0) return;
    
    setIsSubmitting(true);
    try {
      const existingInvoiceUrls = selectedRequest.invoiceUrls || [];
      const existingInvoices = selectedRequest.invoices || [];
      const newInvoiceUrls = [...existingInvoiceUrls];
      const newInvoices = [...existingInvoices];
      
      for (const invoice of pendingInvoices) {
        const timestamp = Date.now();
        // file is optional — numbers-only entries skip the upload step.
        const url = invoice.file
          ? await uploadFile(
              invoice.file,
              `treatment-invoices/${selectedRequest.id}/${timestamp}_${invoice.file.name}`,
            )
          : '';
        newInvoiceUrls.push(url);
        newInvoices.push({
          url,
          name: invoice.file?.name || `Invoice ${invoice.invoiceNumber || timestamp}`,
          uploadedAt: new Date(),
          uploadedBy: currentUser?.uid,
          providerId: invoice.providerId,
          providerName: invoice.providerName,
          providerNameAr: invoice.providerNameAr,
          vatNumber: invoice.vatNumber,
          invoiceDate: new Date(invoice.invoiceDate),
          invoiceNumber: invoice.invoiceNumber,
          totalBeforeDiscount: invoice.totalBeforeDiscount,
          totalAfterDiscount: invoice.totalAfterDiscount,
          taxableAmount: invoice.taxableAmount,
          nonTaxableAmount: invoice.nonTaxableAmount,
          taxAmount: invoice.taxAmount,
        });
      }
      
      await updateRequest(selectedRequest.id, {
        invoiceUrls: newInvoiceUrls,
        invoices: newInvoices,
        invoicesUploadedAt: new Date(),
        invoicesUploadedBy: currentUser?.uid,
        updatedAt: new Date(),
      });
      
      // Update selected request locally
      setSelectedRequest(prev => prev ? {
        ...prev,
        invoiceUrls: newInvoiceUrls,
        invoices: newInvoices,
      } : null);
      
      toast.success(t('dashboard.coordinator.invoiceAdded'));
      setPendingInvoices([]);
    } catch (error: any) {
      console.error('Add invoice error:', error);
      const detail = error?.code || error?.message || String(error);
      toast.error(`${t('dashboard.coordinator.invoiceError')}: ${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateInvoiceMetadata = async () => {
    if (!selectedRequest || editingFileIndex === null) return;

    const existingInvoices = selectedRequest.invoices || [];
    if (editingFileIndex >= existingInvoices.length) return;

    setIsSubmitting(true);
    try {
      const updatedInvoices = [...existingInvoices];
      const editProvider = activeProviders.find(p => p.id === editingInvoiceData.providerId);
      updatedInvoices[editingFileIndex] = {
        ...updatedInvoices[editingFileIndex],
        providerId: editingInvoiceData.providerId,
        providerName: editingInvoiceData.providerName,
        providerNameAr: editProvider?.nameAr || editingInvoiceData.providerName,
        vatNumber: editingInvoiceData.vatNumber,
        invoiceDate: new Date(editingInvoiceData.invoiceDate),
        invoiceNumber: editingInvoiceData.invoiceNumber,
        totalBeforeDiscount: Number(editingInvoiceData.totalBeforeDiscount) || 0,
        totalAfterDiscount: Number(editingInvoiceData.totalAfterDiscount) || 0,
        taxableAmount: Number(editingInvoiceData.taxableAmount) || 0,
        nonTaxableAmount: Number(editingInvoiceData.nonTaxableAmount) || 0,
        taxAmount: Number(editingInvoiceData.taxAmount) || 0,
      };

      await updateRequest(selectedRequest.id, {
        invoices: updatedInvoices,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, invoices: updatedInvoices } : null);
      setIsEditingInvoice(false);
      toast.success(t('dashboard.coordinator.invoiceUpdated'));
    } catch (error) {
      toast.error(t('dashboard.coordinator.invoiceUpdateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateReportMetadata = async () => {
    if (!selectedRequest || editingFileIndex === null) return;

    const existingReports = selectedRequest.medicalReports || [];
    if (editingFileIndex >= existingReports.length) return;

    setIsSubmitting(true);
    try {
      const updatedReports = [...existingReports];
      const editProvider = activeProviders.find(p => p.id === editingReportData.providerId);
      updatedReports[editingFileIndex] = {
        ...updatedReports[editingFileIndex],
        providerId: editingReportData.providerId,
        providerName: editProvider?.name || updatedReports[editingFileIndex].providerName || '',
        department: editingReportData.department,
        reportDate: editingReportData.reportDate ? new Date(editingReportData.reportDate) : updatedReports[editingFileIndex].reportDate,
      };

      await updateRequest(selectedRequest.id, {
        medicalReports: updatedReports,
        updatedAt: new Date(),
      });

      setSelectedRequest(prev => prev ? { ...prev, medicalReports: updatedReports } : null);
      setIsEditingReport(false);
      toast.success(t('dashboard.coordinator.reportUpdated'));
    } catch (error) {
      toast.error(t('dashboard.coordinator.reportUpdateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete report
  const handleDeleteReport = async (reportIndex: number) => {
    if (!selectedRequest) return;
    if (!confirm(t('dashboard.coordinator.confirmDeleteReport'))) return;
    
    const existingReports = selectedRequest.medicalReports || [];
    const updatedReports = existingReports.filter((_, i) => i !== reportIndex);
    
    setIsSubmitting(true);
    try {
      await updateRequest(selectedRequest.id, {
        medicalReports: updatedReports,
        updatedAt: new Date(),
      });
      
      setSelectedRequest(prev => prev ? {
        ...prev,
        medicalReports: updatedReports,
      } : null);
      
      toast.success(t('dashboard.coordinator.reportDeleted'));
    } catch (error) {
      toast.error(t('dashboard.coordinator.deleteError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open review modal
  const openReviewModal = (request: TreatmentSupportRequest) => {
    setSelectedRequest(request);
    setReviewData({
      socialStatusFits: request.socialStatusFits || false,
      medicalConditionFits: request.medicalConditionFits || false,
      coordinatorNotes: request.coordinatorNotes || '',
    });
    setIsReviewOpen(true);
  };

  // Filter requests by search
  const filteredRequests = requests.filter(r => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      r.applicantName?.toLowerCase().includes(query) ||
      r.nameArabic?.toLowerCase().includes(query) ||
      r.nameEnglish?.toLowerCase().includes(query) ||
      r.nationalId?.toLowerCase().includes(query) ||
      r.idNumber?.toLowerCase().includes(query) ||
      r.medicalCondition?.toLowerCase().includes(query)
    );
  });

  // Filter requests by status
  const pendingReview = filteredRequests.filter(r => r.status === 'pending' || r.status === 'under_review');
  const pendingApproval = filteredRequests.filter(r => r.status === 'pending_approval');
  const approved = filteredRequests.filter(r => r.status === 'approved');
  const approvedTreatment = filteredRequests.filter(r => r.status === 'approved_treatment');
  const treatmentStarted = filteredRequests.filter(r => r.status === 'treatment_started');
  const treated = filteredRequests.filter(r => r.status === 'pending_invoices');
  const completed = filteredRequests.filter(r => r.status === 'completed');
  const rejected = filteredRequests.filter(r => r.status === 'rejected');

  // Sort handler
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort function
  const sortRequests = (list: TreatmentSupportRequest[]) => {
    return [...list].sort((a, b) => {
      let aVal: any, bVal: any;
      
      switch (sortColumn) {
        case 'nationalId':
          aVal = a.nationalId || a.idNumber || '';
          bVal = b.nationalId || b.idNumber || '';
          break;
        case 'createdAt':
          aVal = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
          break;
        case 'name':
          aVal = dir === 'rtl' ? (a.nameArabic || a.applicantName || '') : (a.nameEnglish || a.applicantName || '');
          bVal = dir === 'rtl' ? (b.nameArabic || b.applicantName || '') : (b.nameEnglish || b.applicantName || '');
          break;
        case 'age':
          aVal = a.dateOfBirth ? new Date(a.dateOfBirth).getTime() : 0;
          bVal = b.dateOfBirth ? new Date(b.dateOfBirth).getTime() : 0;
          break;
        case 'condition':
          aVal = a.medicalCondition || '';
          bVal = b.medicalCondition || '';
          break;
        case 'cost':
          aVal = a.cost || 0;
          bVal = b.cost || 0;
          break;
        case 'totalReceived':
          aVal = a.donationEntries?.reduce((sum: number, e: any) => sum + (e.amount || 0), 0) || 0;
          bVal = b.donationEntries?.reduce((sum: number, e: any) => sum + (e.amount || 0), 0) || 0;
          break;
        case 'updatedAt':
          aVal = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          bVal = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          break;
        case 'lastUpdate':
          aVal = a.updates?.length ? new Date(a.updates[a.updates.length - 1].createdAt).getTime() : 0;
          bVal = b.updates?.length ? new Date(b.updates[b.updates.length - 1].createdAt).getTime() : 0;
          break;
        default:
          aVal = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          bVal = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      }
      
      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  // Render sort icon
  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ms-1 opacity-50" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 ms-1" /> : <ArrowDown className="h-3 w-3 ms-1" />;
  };

  const renderRequestsTable = (requestsList: TreatmentSupportRequest[], showActions: 'review' | 'view' | 'startTreatment' | 'markTreated') => {
    const totalCost = requestsList.reduce((sum, r) => sum + getNetMedicalCost(r), 0);
    const totalDonations = requestsList.reduce((sum, r) => sum + (r.donationEntries?.reduce((s: number, e: any) => s + (e.amount || 0), 0) || 0), 0);

    return (
    <div className="rounded-md border overflow-x-auto">
      <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.coordinator.table.caseNumber')}
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('createdAt')}>
              <span className="flex items-center">{t('dashboard.coordinator.table.creationDate')}<SortIcon column="createdAt" /></span>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('name')}>
              <span className="flex items-center">{t('dashboard.coordinator.table.name')}<SortIcon column="name" /></span>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('age')}>
              <span className="flex items-center">{t('dashboard.coordinator.table.age')}<SortIcon column="age" /></span>
            </TableHead>
            <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.coordinator.table.classification')}
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('cost')}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center">{t('dashboard.coordinator.table.cost')}<SortIcon column="cost" /></span>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="inline-flex items-center gap-1">{totalCost.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('totalReceived')}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center">{t('dashboard.coordinator.table.totalReceived')}<SortIcon column="totalReceived" /></span>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="inline-flex items-center gap-1">{totalDonations.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('updatedAt')}>
              <span className="flex items-center">{t('dashboard.coordinator.table.lastModified')}<SortIcon column="updatedAt" /></span>
            </TableHead>
            <TableHead className={`${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer hover:bg-muted/50`} onClick={() => handleSort('lastUpdate')}>
              <span className="flex items-center">{t('dashboard.coordinator.table.lastUpdate')}<SortIcon column="lastUpdate" /></span>
            </TableHead>
            <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.coordinator.table.links')}
            </TableHead>
            <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.coordinator.table.actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requestsList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} className="text-center text-muted-foreground py-8">
                {t('dashboard.coordinator.noCases')}
              </TableCell>
            </TableRow>
          ) : (
            sortRequests(requestsList).map((request) => (
              <TableRow key={request.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedRequest(request); setIsViewOpen(true); }}>
                <TableCell className="font-mono text-muted-foreground">{request.caseNumber ?? '-'}</TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                  {request.createdAt
                    ? formatLocalDate(
                        request.createdAt instanceof Date
                          ? request.createdAt
                          : (request.createdAt as any)?.toDate?.() || new Date(request.createdAt),
                        'PP',
                      )
                    : '-'}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex flex-col">
                    <span>{dir === 'rtl' ? (request.nameArabic || request.applicantName) : (request.nameEnglish || request.applicantName)}</span>
                    <span className="text-xs text-muted-foreground font-mono" dir="ltr">{request.nationalId}</span>
                  </div>
                </TableCell>
                <TableCell>{request.dateOfBirth ? Math.floor((Date.now() - new Date(request.dateOfBirth).getTime()) / 31557600000) : (request.age || '-')}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <CategorySelect
                    value={request.categoryId}
                    activeCategories={activeCategories}
                    onChange={(newId) => updateRequestCategory(request, newId)}
                  />
                </TableCell>
                <TableCell><span className="inline-flex items-center gap-1">{getNetMedicalCost(request).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></span></TableCell>
                <TableCell>
                  {(() => {
                    const allocated = (request.donationEntries || []).reduce(
                      (sum: number, e: any) => sum + (e.amount || 0),
                      0,
                    );
                    const netCost = getNetMedicalCost(request);
                    const fullyFunded = netCost > 0 && allocated >= netCost;
                    return (
                      <span
                        className={`inline-flex items-center gap-1${
                          fullyFunded ? ' text-green-700 bg-green-50 px-2 py-0.5 rounded-full' : ''
                        }`}
                      >
                        {allocated.toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" />
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {request.updatedAt ? formatLocalDate(new Date(request.updatedAt), 'PP') : '-'}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {request.updates && request.updates.length > 0 
                    ? formatLocalDate(
                        request.updates[request.updates.length - 1].createdAt instanceof Date 
                          ? request.updates[request.updates.length - 1].createdAt 
                          : (request.updates[request.updates.length - 1].createdAt as any)?.toDate?.() || new Date(request.updates[request.updates.length - 1].createdAt), 
                        'PP'
                      )
                    : '-'}
                </TableCell>
                <TableCell>
                  {(request.donationEntries?.length || 0) > 0 ? (
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">{request.donationEntries?.length}</Badge>
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setSelectedRequest(request); setIsViewOpen(true); }}>
                        <Eye className="h-4 w-4 me-2" />
                        {t('dashboard.coordinator.view')}
                      </DropdownMenuItem>
                      
                      {showActions === 'review' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openReviewModal(request)}>
                            <ClipboardCheck className="h-4 w-4 me-2 text-blue-600" />
                            {t('dashboard.coordinator.review')}
                          </DropdownMenuItem>
                        </>
                      )}
                      
                      {showActions === 'startTreatment' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleStartTreatment(request)}
                            disabled={!canStartTreatment(request).ok}
                          >
                            <CheckCircle className="h-4 w-4 me-2 text-indigo-600" />
                            {t('dashboard.coordinator.startTreatmentButton')}
                          </DropdownMenuItem>
                        </>
                      )}

                      {showActions === 'markTreated' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleMarkCompleted(request)}
                            disabled={!request.invoiceUrls?.length}
                          >
                            <CheckCircle className="h-4 w-4 me-2 text-cyan-600" />
                            {t('dashboard.coordinator.markTreated')}
                          </DropdownMenuItem>
                        </>
                      )}

                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      </TooltipProvider>
    </div>
  );
  };

  return (
    <div className="space-y-6" dir={dir}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Users className="h-8 w-8 text-primary" />
            {t('dashboard.coordinator.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('dashboard.coordinator.description')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsSettingsOpen(true)} className="gap-2">
            <Settings className="h-4 w-4" />
            {t('dashboard.coordinator.settings.title')}
          </Button>
          <Button onClick={() => setIsAddOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('dashboard.coordinator.addCase')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('dashboard.coordinator.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="ps-10"
          />
        </div>
        {/* View Switcher */}
        <div className="inline-flex items-center rounded-lg border bg-muted p-0.5 gap-0.5">
          <button
            onClick={() => setActiveView('cases')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeView === 'cases' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            <FileText className="h-4 w-4" />
            {t('dashboard.admin.helps.viewCases')}
          </button>
          <button
            onClick={() => setActiveView('links')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeView === 'links' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            <HandHelping className="h-4 w-4" />
            {t('dashboard.admin.helps.viewLinks')}
          </button>
          {/* Marketing payments view hidden per ops request (2026-07-14) — view + data kept, only the switcher button removed */}
          {false && (
            <button
              onClick={() => setActiveView('marketing')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeView === 'marketing' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
            >
              <Wallet className="h-4 w-4" />
              {t('dashboard.admin.helps.viewMarketing')}
            </button>
          )}
          <button
            onClick={() => setActiveView('vat')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeView === 'vat' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            <PercentSquare className="h-4 w-4" />
            {t('dashboard.admin.helps.viewVat')}
          </button>
          <button
            onClick={() => setActiveView('cashbacks')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeView === 'cashbacks' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            <Banknote className="h-4 w-4" />
            {t('dashboard.admin.helps.viewCashbacks')}
            {cashbacks.filter(c => c.status === 'pending').length > 0 && (
              <Badge className="bg-yellow-500 text-white text-xs px-1.5 py-0">{cashbacks.filter(c => c.status === 'pending').length}</Badge>
            )}
          </button>
        </div>
      </div>

      {/* Cashbacks View */}
      {activeView === 'cashbacks' && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('dashboard.coordinator.cashbacks.title')}</h3>
              <Button onClick={() => { setCashbackForm({ providerId: '', totalAmount: '', date: '', notes: '' }); setCashbackCases([{ caseId: '', amount: '' }]); setCashbackReceiptFile(null); setIsAddCashbackOpen(true); }} className="gap-2">
                <Plus className="h-4 w-4" />
                {t('dashboard.coordinator.cashbacks.addCashback')}
              </Button>
            </div>
            {cashbacksLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : cashbacks.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t('dashboard.coordinator.cashbacks.noCashbacks')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-end">#</TableHead>
                    <TableHead className="text-end">{t('dashboard.coordinator.cashbacks.hospital')}</TableHead>
                    <TableHead className="text-end">{t('dashboard.coordinator.cashbacks.cases')}</TableHead>
                    <TableHead className="text-end">{t('dashboard.coordinator.cashbacks.totalAmount')}</TableHead>
                    <TableHead className="text-end">{t('dashboard.coordinator.cashbacks.date')}</TableHead>
                    <TableHead className="text-end">{t('dashboard.coordinator.cashbacks.receipt')}</TableHead>
                    <TableHead className="text-end">{t('dashboard.coordinator.cashbacks.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cashbacks.map((cb, idx) => (
                    <TableRow key={cb.id}>
                      <TableCell className="text-end text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="text-end text-sm">{cb.providerName}</TableCell>
                      <TableCell className="text-end text-xs">
                        {cb.cases.map((c, i) => (
                          <div key={i}>{c.caseName || c.caseId} — {c.amount.toLocaleString('en-US')} SAR</div>
                        ))}
                      </TableCell>
                      <TableCell className="text-end font-medium">{cb.totalAmount.toLocaleString('en-US')}</TableCell>
                      <TableCell className="text-end text-sm">{format(cb.date, 'yyyy-MM-dd')}</TableCell>
                      <TableCell className="text-end">
                        {cb.receiptUrl && (
                          <a href={cb.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                            <FileText className="h-4 w-4" />
                          </a>
                        )}
                      </TableCell>
                      <TableCell className="text-end">
                        <Badge className={cb.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : cb.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {t(`dashboard.coordinator.cashbacks.${cb.status}`)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Donations / Links View (read-only) */}
      {activeView === 'links' && (() => {
        const q = linksSearchQuery.trim().toLowerCase();
        const filtered = q
          ? allLinks.filter(l =>
              String(l.platformCaseNumber || '').toLowerCase().includes(q) ||
              String(l.platform || '').toLowerCase().includes(q) ||
              String(l.linkTitle || '').toLowerCase().includes(q))
          : allLinks;
        const totalReceived = filtered.reduce((s, l) => s + (l.actualReceived || 0), 0);
        const totalMarketing = filtered.reduce((s, l) => s + (l.marketingShare || 0), 0);
        const totalNet = filtered.reduce((s, l) => s + (l.availableForCases || 0), 0);
        const totalAllocated = filtered.reduce((s, l) => s + (l.totalAllocated || 0), 0);
        const totalRemaining = filtered.reduce((s, l) => s + (l.remaining || 0), 0);
        const totalVAT = filtered.reduce((s, l) => s + (l.refundableVAT || 0), 0);
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder={t('dashboard.admin.helps.linksView.searchPlaceholder')}
                  value={linksSearchQuery}
                  onChange={(e) => setLinksSearchQuery(e.target.value)}
                  className="ps-10"
                />
              </div>
              <div className="flex items-center justify-center sm:justify-start">
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground whitespace-nowrap">
                  <span className="font-semibold text-foreground">{filtered.length}</span>
                  {t('dashboard.admin.helps.linksView.totalLinks')}
                </span>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {[
                { label: t('dashboard.admin.helps.linksView.totalDonations'), val: totalReceived },
                { label: t('dashboard.admin.helps.linksView.totalMarketing'), val: totalMarketing },
                { label: t('dashboard.admin.helps.linksView.totalNet'), val: totalNet },
                { label: t('dashboard.admin.helps.linksView.totalRemaining'), val: totalRemaining },
                { label: t('dashboard.admin.helps.linksView.totalAllocatedCard'), val: totalAllocated },
                { label: t('dashboard.admin.helps.linksView.totalVAT'), val: totalVAT },
              ].map((c, i) => (
                <div key={i} className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                  <p className="text-xs text-muted-foreground whitespace-nowrap">{c.label}</p>
                  <p className="text-lg font-bold inline-flex items-center gap-1">{Math.round(c.val).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
                </div>
              ))}
            </div>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">{t('dashboard.admin.helps.viewLinks')}</h3>
                {filtered.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t('dashboard.admin.helps.linksView.noLinks')}</p>
                ) : (
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-end">#</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.linksView.platform')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.linksView.platformCaseNo')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.linksView.linkTitle')}</TableHead>
                      <TableHead className="text-end min-w-[200px]">{t('dashboard.admin.helps.linksView.cases')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.linksView.received')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.linksView.marketingAmount')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.linksView.netAmount')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.linksView.totalAllocated')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.linksView.remaining')}</TableHead>
                      <TableHead className="text-end"></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {filtered.map((l, idx) => (
                        <TableRow key={l._key}>
                          <TableCell className="text-end text-muted-foreground">{idx + 1}</TableCell>
                          <TableCell className="text-end text-sm">{t(`dashboard.coordinator.platforms.${l.platform}`)}</TableCell>
                          <TableCell className="text-end font-mono text-xs" dir="ltr">{l.platformCaseNumber}</TableCell>
                          <TableCell className="text-end text-sm">
                            <div className="flex flex-col gap-1 items-end">
                              <span>{l.linkTitle || '—'}</span>
                              {donationStartByLink[l._key] && (
                                <span className="block text-xs text-muted-foreground">
                                  {t('dashboard.admin.helps.linksView.startDate')}: {formatLocalDate(donationStartByLink[l._key], 'PP')}
                                </span>
                              )}
                              <span className="block text-xs text-muted-foreground">
                                {(() => {
                                  const cat = categories.find(c => c.id === l.treatmentCategoryId);
                                  return cat ? (dir === 'rtl' ? (cat.nameAr || cat.name) : cat.name) : '—';
                                })()}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1.5 justify-end">
                              {l.cases.length === 0 ? (
                                <span className="text-muted-foreground text-xs">—</span>
                              ) : l.cases.map((c, ci) => (
                                <span
                                  key={ci}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-xs font-medium border"
                                  title={`#${c.caseNumber ?? '-'} · ${c.idNumber || '-'} · ${(c.allocated || 0).toLocaleString('en-US')} SAR`}
                                >
                                  <span className="font-semibold">{dir === 'rtl' ? c.nameAr : c.nameEn}</span>
                                  {c.idNumber && <span className="text-muted-foreground font-mono" dir="ltr">{c.idNumber}</span>}
                                </span>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-end">{(l.actualReceived || 0).toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-end text-muted-foreground">{(l.marketingShare || 0).toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-end">{(l.availableForCases || 0).toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-end">{(l.totalAllocated || 0).toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-end font-medium">{(l.remaining || 0).toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setManagingDonationsLink(l)}>
                                  <SaudiRiyalIcon className="h-4 w-4 me-2" />{t('dashboard.admin.helps.linksView.manageDonations')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Manage Donations Dialog (read-only transaction list) */}
      <Dialog open={!!managingDonationsLink} onOpenChange={(open) => { if (!open) { setManagingDonationsLink(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir={dir}>
          <DialogHeader>
            <DialogTitle>
              {t('dashboard.admin.helps.linksView.manageDonations')}
              {managingDonationsLink && (
                <span className="block text-sm font-normal text-muted-foreground mt-1">
                  {managingDonationsLink.linkTitle || managingDonationsLink.platformCaseNumber} ·{' '}
                  {(donationSumByLink[managingDonationsLink._key] || 0).toLocaleString('en-US')} {t('common.sar')}
                  {' · '}{(donationsByLink[managingDonationsLink._key] || []).length} {t('dashboard.admin.helps.linksView.donationsCount')}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">{t('dashboard.admin.helps.linksView.manageDonations')}</DialogDescription>
          </DialogHeader>

          {managingDonationsLink && (
            <div className="space-y-4">
              <div className="border rounded-md max-h-[45vh] overflow-y-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-start">{t('dashboard.admin.helps.linksView.donationDate')}</TableHead>
                    <TableHead className="text-start">{t('dashboard.admin.helps.linksView.donationAmount')}</TableHead>
                    <TableHead className="text-start">{t('dashboard.admin.helps.linksView.donorName')}</TableHead>
                    <TableHead className="text-start">{t('dashboard.admin.helps.linksView.source')}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(donationsByLink[managingDonationsLink._key] || []).length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">{t('dashboard.admin.helps.linksView.noDonations')}</TableCell></TableRow>
                    ) : (
                      (donationsByLink[managingDonationsLink._key] || []).map((d) => (
                        <TableRow key={d.id}>
                          <TableCell className="whitespace-nowrap">{formatLocalDate(d.date, 'PP')}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1">{(d.amount || 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                          </TableCell>
                          <TableCell>{d.donorName || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={d.source === 'jood' ? 'secondary' : 'outline'} className="text-xs">
                              {t(`dashboard.admin.helps.linksView.donationSource.${d.source}`)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Marketing Payments View (read-only) */}
      {activeView === 'marketing' && (() => {
        const marketingCost = allLinks.reduce((s, l) => s + (l.marketingShare || 0), 0);
        const totalPaid = marketingPayments.reduce((s, p) => s + (p.amount || 0), 0);
        const totalDue = marketingCost - totalPaid;
        return (
          <div className="space-y-6">
            <div className="flex gap-2 overflow-x-auto">
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.marketing.marketingCost')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1">{Math.round(marketingCost).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.marketing.totalPaid')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1 text-green-600">{Math.round(totalPaid).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.marketing.totalDue')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1 text-amber-600">{Math.round(totalDue).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
            </div>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">{t('dashboard.admin.helps.marketing.title')}</h3>
                {marketingPayments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t('dashboard.admin.helps.marketing.noPayments')}</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-end">#</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.marketing.amount')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.marketing.date')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.marketing.receipt')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.marketing.notes')}</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {marketingPayments.map((p, idx) => (
                        <TableRow key={p.id}>
                          <TableCell className="text-end text-muted-foreground">{idx + 1}</TableCell>
                          <TableCell className="text-end font-medium">{p.amount.toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-end text-sm">{format(p.date, 'yyyy-MM-dd')}</TableCell>
                          <TableCell className="text-end">
                            {p.receiptUrl && (
                              <a href={p.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                                <FileText className="h-4 w-4" />
                              </a>
                            )}
                          </TableCell>
                          <TableCell className="text-end text-xs text-muted-foreground">{p.notes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* VAT Refund View (read-only) */}
      {activeView === 'vat' && (() => {
        const vatDue = allLinks.reduce((s, l) => s + (l.refundableVAT || 0), 0);
        const totalRefunded = vatRefunds.filter(r => r.status === 'approved').reduce((s, r) => s + (r.amount || 0), 0);
        const remaining = vatDue - totalRefunded;
        return (
          <div className="space-y-6">
            <div className="flex gap-2 overflow-x-auto">
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.vat.vatDue')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1">{Math.round(vatDue).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.vat.totalRefunded')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1 text-green-600">{Math.round(totalRefunded).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground whitespace-nowrap">{t('dashboard.admin.helps.vat.remaining')}</p>
                <p className="text-lg font-bold inline-flex items-center gap-1 text-amber-600">{Math.round(remaining).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></p>
              </div>
            </div>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">{t('dashboard.admin.helps.vat.title')}</h3>
                {vatRefunds.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t('dashboard.admin.helps.vat.noRefunds')}</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-end">#</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.amount')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.date')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.receipt')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.notes')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.admin.helps.vat.status')}</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {vatRefunds.map((r, idx) => (
                        <TableRow key={r.id}>
                          <TableCell className="text-end text-muted-foreground">{idx + 1}</TableCell>
                          <TableCell className="text-end font-medium">{r.amount.toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-end text-sm">{format(r.date, 'yyyy-MM-dd')}</TableCell>
                          <TableCell className="text-end">
                            {r.receiptUrl && (
                              <a href={r.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                                <FileText className="h-4 w-4" />
                              </a>
                            )}
                          </TableCell>
                          <TableCell className="text-end text-xs text-muted-foreground">{r.notes}</TableCell>
                          <TableCell className="text-end">
                            <Badge className={r.status === 'approved' ? 'bg-green-100 text-green-800' : r.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                              {t(`dashboard.admin.helps.vat.${r.status || 'pending'}`)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Cases Tabs */}
      {activeView === 'cases' && <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Tabs value={topTab} onValueChange={handleTopTabChange} className="w-full" dir={dir}>
              <TabsList className="flex w-full mb-6" dir={dir}>
                <TabsTrigger value="pending" className="flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.pendingReview')}
                  {pendingReview.length > 0 && <Badge className="bg-primary text-white">{pendingReview.length}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="approval" className="flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.pendingApproval')}
                  {pendingApproval.length > 0 && <Badge className="bg-primary text-white">{pendingApproval.length}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="approved" className="flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.approved')}
                  {approved.length > 0 && <Badge className="bg-primary text-white">{approved.length}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="approvedTreatment" className="flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.approvedTreatment')}
                  {approvedTreatment.length > 0 && <Badge className="bg-primary text-white">{approvedTreatment.length}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="treatmentStarted" className="flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.treatmentStarted')}
                  {treatmentStarted.length > 0 && <Badge className="bg-primary text-white">{treatmentStarted.length}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="treated" className="flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.treated')}
                  {treated.length > 0 && <Badge className="bg-primary text-white">{treated.length}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="completed" className="flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.completed')}
                  {completed.length > 0 && <Badge className="bg-primary text-white">{completed.length}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="rejected" className="flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.rejected')}
                  {rejected.length > 0 && <Badge className="bg-primary text-white">{rejected.length}</Badge>}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pending">
                {renderRequestsTable(pendingReview, 'review')}
              </TabsContent>
              <TabsContent value="approval">
                {renderRequestsTable(pendingApproval, 'view')}
              </TabsContent>
              <TabsContent value="approved">
                {renderRequestsTable(approved, 'view')}
              </TabsContent>
              <TabsContent value="approvedTreatment">
                {renderRequestsTable(approvedTreatment, 'startTreatment')}
              </TabsContent>
              <TabsContent value="treatmentStarted">
                {renderRequestsTable(treatmentStarted, 'markTreated')}
              </TabsContent>
              <TabsContent value="treated">
                {renderRequestsTable(treated, 'view')}
              </TabsContent>
              <TabsContent value="completed">
                {renderRequestsTable(completed, 'view')}
              </TabsContent>
              <TabsContent value="rejected">
                {renderRequestsTable(rejected, 'view')}
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>}

      {/* Add Cashback Dialog */}
      <Dialog open={isAddCashbackOpen} onOpenChange={setIsAddCashbackOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" dir={dir}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{t('dashboard.coordinator.cashbacks.addCashback')}</DialogTitle>
            <DialogDescription className="sr-only">{t('dashboard.coordinator.cashbacks.description')}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto py-2 px-1">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              {/* Right/First Column */}
              {/* Provider Name */}
              <div className="space-y-2">
                <Label>{t('dashboard.coordinator.cashbacks.providerName')} *</Label>
                <Select value={cashbackForm.providerId} onValueChange={(val) => { setCashbackForm(f => ({ ...f, providerId: val })); setCashbackCases([{ caseId: '', amount: '' }]); }}>
                  <SelectTrigger><SelectValue placeholder={t('dashboard.coordinator.cashbacks.providerPlaceholder')} /></SelectTrigger>
                  <SelectContent>
                    {activeProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {language === 'ar' ? (provider.nameAr || provider.name) : provider.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Left/Second Column */}
              {/* Total Amount (auto-calculated) */}
              <div className="space-y-2">
                <Label>{t('dashboard.coordinator.cashbacks.totalAmount')}</Label>
                <Input
                  type="number"
                  value={cashbackCases.reduce((s, c) => s + (Number(c.amount) || 0), 0) || ''}
                  readOnly
                  className="bg-muted"
                  dir="ltr"
                />
              </div>
              {/* Date */}
              <div className="space-y-2">
                <Label>{t('dashboard.coordinator.cashbacks.date')} *</Label>
                <Input
                  type="date"
                  value={cashbackForm.date}
                  onChange={(e) => setCashbackForm(f => ({ ...f, date: e.target.value }))}
                  dir="ltr"
                />
              </div>
              {/* Receipt PDF */}
              <div className="space-y-2">
                <Label>{t('dashboard.coordinator.cashbacks.uploadReceipt')} *</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setCashbackReceiptFile(e.target.files?.[0] || null)}
                />
                {cashbackReceiptFile && <p className="text-xs text-muted-foreground">{cashbackReceiptFile.name}</p>}
              </div>
              {/* Case Allocations - full width */}
              <div className="col-span-2 space-y-2">
                <Label>{t('dashboard.coordinator.cashbacks.cases')} *</Label>
                {cashbackCases.map((cc, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select
                      value={cc.caseId}
                      onChange={(e) => {
                        const updated = [...cashbackCases];
                        updated[idx].caseId = e.target.value;
                        // Auto-fill amount from provider's invoices × cashback %
                        if (e.target.value && cashbackForm.providerId) {
                          const caseReq = requests.find(r => r.id === e.target.value);
                          const provInvoices = caseReq?.invoices?.filter(inv => inv.providerId === cashbackForm.providerId) || [];
                          if (provInvoices.length > 0) {
                            const provInvTotal = provInvoices.reduce((s, inv) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0);
                            if (provInvTotal > 0) {
                              const selectedProv = activeProviders.find(p => p.id === cashbackForm.providerId);
                              const pct = selectedProv?.cashbackPercentage || 0;
                              // Subtract already-claimed cashback for this case+provider
                              const alreadyClaimed = cashbacks
                                .filter(cb => cb.status !== 'rejected' && cb.providerId === cashbackForm.providerId)
                                .reduce((s, cb) => s + (cb.cases.find(c => c.caseId === e.target.value)?.amount || 0), 0);
                              const expectedCashback = provInvTotal * pct / 100;
                              const remainingCashback = Math.max(0, Math.round(expectedCashback - alreadyClaimed));
                              updated[idx].amount = String(remainingCashback);
                            }
                          }
                        }
                        setCashbackCases(updated);
                      }}
                      className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                      dir={dir}
                    >
                      <option value="">{t('dashboard.coordinator.cashbacks.selectCasePlaceholder')}</option>
                      {(() => {
                        // Cases already claimed for this provider (pending or confirmed)
                        const claimedCaseIds = new Set(
                          cashbacks
                            .filter(cb => cb.status !== 'rejected' && cb.providerId === cashbackForm.providerId)
                            .flatMap(cb => cb.cases.map(c => c.caseId))
                        );
                        // Also exclude cases already selected in other rows of this form
                        const selectedInForm = new Set(cashbackCases.filter((c, i) => i !== idx && c.caseId).map(c => c.caseId));
                        return requests
                          .filter(r => {
                            if (r.status !== 'completed') return false;
                            // Only show cases with invoices from the selected provider
                            if (!cashbackForm.providerId) return false;
                            const hasProviderInvoices = r.invoices?.some(inv => inv.providerId === cashbackForm.providerId);
                            if (!hasProviderInvoices) return false;
                            // Exclude if already fully claimed or selected in another row
                            if (selectedInForm.has(r.id)) return false;
                            if (claimedCaseIds.has(r.id)) {
                              // Check if there's remaining cashback
                              const provInvTotal = (r.invoices || []).filter(inv => inv.providerId === cashbackForm.providerId)
                                .reduce((s, inv) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0);
                              const selectedProv = activeProviders.find(p => p.id === cashbackForm.providerId);
                              const pct = selectedProv?.cashbackPercentage || 0;
                              const expectedCashback = provInvTotal * pct / 100;
                              const alreadyClaimed = cashbacks
                                .filter(cb => cb.status !== 'rejected' && cb.providerId === cashbackForm.providerId)
                                .reduce((s, cb) => s + (cb.cases.find(c => c.caseId === r.id)?.amount || 0), 0);
                              if (alreadyClaimed >= expectedCashback) return false;
                            }
                            return true;
                          })
                          .map(r => {
                            const provInvTotal = (r.invoices || []).filter(inv => inv.providerId === cashbackForm.providerId)
                              .reduce((s, inv) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0);
                            const selProv = activeProviders.find(p => p.id === cashbackForm.providerId);
                            const cbPct = selProv?.cashbackPercentage || 0;
                            const expectedCb = Math.round(provInvTotal * cbPct / 100);
                            const claimed = cashbacks
                              .filter(cb => cb.status !== 'rejected' && cb.providerId === cashbackForm.providerId)
                              .reduce((s, cb) => s + (cb.cases.find(c => c.caseId === r.id)?.amount || 0), 0);
                            const remaining = Math.max(0, expectedCb - Math.round(claimed));
                            return (
                              <option key={r.id} value={r.id}>
                                #{r.caseNumber} — {r.nameArabic || r.applicantName || r.nameEnglish}{remaining > 0 ? ` (${remaining.toLocaleString('en-US')} SAR)` : ''}
                              </option>
                            );
                          });
                      })()}
                    </select>
                    <Input
                      type="number"
                      value={cc.amount}
                      onChange={(e) => {
                        const updated = [...cashbackCases];
                        updated[idx].amount = e.target.value;
                        setCashbackCases(updated);
                      }}
                      placeholder={t('dashboard.coordinator.cashbacks.caseAmount')}
                      className="w-32"
                      dir="ltr"
                    />
                    {cashbackCases.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => setCashbackCases(prev => prev.filter((_, i) => i !== idx))}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setCashbackCases(prev => [...prev, { caseId: '', amount: '' }])} className="gap-1">
                  <Plus className="h-3 w-3" />
                  {t('dashboard.coordinator.cashbacks.addCase')}
                </Button>
              </div>
              {/* Notes - full width */}
              <div className="col-span-2 space-y-2">
                <Label>{t('dashboard.coordinator.cashbacks.notes')}</Label>
                <Textarea
                  value={cashbackForm.notes}
                  onChange={(e) => setCashbackForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 gap-2">
            <Button variant="outline" onClick={() => setIsAddCashbackOpen(false)}>
              {t('dashboard.coordinator.cancel')}
            </Button>
            <Button
              disabled={isSubmittingCashback}
              onClick={async () => {
                // Validation
                if (!cashbackForm.providerId) { toast.error(t('dashboard.coordinator.cashbacks.providerRequired')); return; }
                if (!cashbackReceiptFile) { toast.error(t('dashboard.coordinator.cashbacks.receiptRequired')); return; }
                const validCases = cashbackCases.filter(c => c.caseId && Number(c.amount) > 0);
                if (validCases.length === 0) { toast.error(t('dashboard.coordinator.cashbacks.caseRequired')); return; }
                const casesSum = validCases.reduce((s, c) => s + Number(c.amount), 0);
                if (casesSum <= 0) { toast.error(t('dashboard.coordinator.cashbacks.amountRequired')); return; }

                setIsSubmittingCashback(true);
                try {
                  // Upload receipt
                  const uuid = crypto.randomUUID();
                  const path = `cashback-receipts/${uuid}/${Date.now()}_${cashbackReceiptFile.name}`;
                  const storageRef = ref(storage, path);
                  await uploadBytes(storageRef, cashbackReceiptFile);
                  const receiptUrl = await getDownloadURL(storageRef);

                  // Build case allocations
                  const caseAllocations: CashbackCaseAllocation[] = validCases.map(c => {
                    const caseReq = requests.find(r => r.id === c.caseId);
                    return {
                      caseId: c.caseId,
                      caseNumber: caseReq?.caseNumber,
                      caseName: caseReq?.nameArabic || caseReq?.applicantName || caseReq?.nameEnglish || '',
                      amount: Number(c.amount),
                    };
                  });

                  const selectedProvider = activeProviders.find(p => p.id === cashbackForm.providerId);
                  await addCashback({
                    providerId: cashbackForm.providerId,
                    providerName: language === 'ar' ? (selectedProvider?.nameAr || selectedProvider?.name || '') : (selectedProvider?.name || ''),
                    totalAmount: casesSum,
                    cases: caseAllocations,
                    receiptUrl,
                    receiptName: cashbackReceiptFile.name,
                    date: new Date(cashbackForm.date),
                    notes: cashbackForm.notes.trim() || undefined,
                    status: 'pending',
                    createdBy: currentUser?.uid || '',
                    createdByName: currentUser?.displayName || currentUser?.email || '',
                  });

                  toast.success(t('dashboard.coordinator.cashbacks.addSuccess'));
                  setIsAddCashbackOpen(false);
                } catch (error) {
                  console.error('Add cashback error:', error);
                  toast.error(t('dashboard.coordinator.cashbacks.addError'));
                } finally {
                  setIsSubmittingCashback(false);
                }
              }}
              className="gap-2"
            >
              {isSubmittingCashback && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('dashboard.coordinator.cashbacks.addCashback')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Case Modal */}
      <Dialog open={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) setAddFormStep(1); }}>
        <DialogContent className="max-w-6xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden" dir={dir}>
          <div className="px-6 pt-6 pb-4 border-b shrink-0 bg-gray-50">
            <DialogHeader dir={dir}>
              <DialogTitle>{t('dashboard.coordinator.addCase')}</DialogTitle>
              <DialogDescription>
                {t('dashboard.coordinator.addCaseDescription')}
              </DialogDescription>
            </DialogHeader>
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-muted-foreground">{t('common.step')} {addFormStep} {t('common.of')} {ADD_TOTAL_STEPS}</span>
                <span className="text-xs font-bold text-primary">{Math.round(addFormStep / ADD_TOTAL_STEPS * 100)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-1.5">
                <div className="bg-primary h-1.5 rounded-full transition-all duration-500" style={{ width: `${addFormStep / ADD_TOTAL_STEPS * 100}%` }} />
              </div>
              <div className="flex justify-between mt-1.5">
                {addStepLabels.map((label, i) => (
                  <span key={i} className={`text-[10px] font-medium ${addFormStep >= i + 1 ? 'text-primary' : 'text-muted-foreground'}`}>{label}</span>
                ))}
              </div>
            </div>
          </div>
          <form onSubmit={handleAddCase} className="flex flex-col min-h-0 flex-1">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {addFormStep === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nameArabic">{t('dashboard.coordinator.form.nameArabic')}</Label>
                  <Input
                    id="nameArabic"
                    value={formData.nameArabic}
                    onChange={(e) => setFormData(prev => ({ ...prev, nameArabic: e.target.value }))}
                    dir="rtl"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard.coordinator.form.nameArabicHint')}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nameEnglish">{t('dashboard.coordinator.form.nameEnglish')}</Label>
                  <Input
                    id="nameEnglish"
                    value={formData.nameEnglish}
                    onChange={(e) => setFormData(prev => ({ ...prev, nameEnglish: e.target.value }))}
                    required
                    dir="ltr"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard.coordinator.form.nameEnglishHint')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="idType">{t('dashboard.coordinator.form.idType')}</Label>
                  <Select
                    value={formData.idType}
                    onValueChange={(value: TreatmentIdType) => setFormData(prev => ({ ...prev, idType: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="national_id">{t('dashboard.coordinator.form.idTypes.nationalId')}</SelectItem>
                      <SelectItem value="residency">{t('dashboard.coordinator.form.idTypes.residency')}</SelectItem>
                      <SelectItem value="umrah_visit_visa">{t('dashboard.coordinator.form.idTypes.umrahVisitVisa')}</SelectItem>
                      <SelectItem value="passport">{t('dashboard.coordinator.form.idTypes.passport')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="idNumber">{t('dashboard.coordinator.form.idNumber')} *</Label>
                  <Input
                    id="idNumber"
                    value={formData.idNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, idNumber: e.target.value }))}
                    required
                    dir="ltr"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard.coordinator.form.idNumberHint')}
                  </p>
                </div>

                <div className="space-y-2">
                  <HijriDatePicker label={t('dashboard.coordinator.form.idExpiryDate')} value={formData.idExpiryDate} onChange={(v) => setFormData(prev => ({ ...prev, idExpiryDate: v }))} />
                </div>

                {/* ID Document Upload */}
                <div className="space-y-2">
                  <Label>{t('dashboard.coordinator.form.idDocument')}</Label>
                  {pendingIdDocument ? (
                    <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-muted/50">
                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm truncate flex-1">{pendingIdDocument.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setPendingIdDocument(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setPendingIdDocument(file);
                        }}
                      />
                      <div className="flex items-center justify-center gap-2 h-10 border-2 border-dashed rounded-md hover:border-primary transition-colors">
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {t('dashboard.coordinator.form.uploadIdDocument')}
                        </span>
                      </div>
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard.coordinator.form.idDocumentHint')}
                  </p>
                </div>
              </div>
              )}

              {addFormStep === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <HijriDatePicker label={t('dashboard.coordinator.form.dateOfBirth')} value={formData.dateOfBirth} onChange={(v) => setFormData(prev => ({ ...prev, dateOfBirth: v }))} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nationality">{t('dashboard.coordinator.form.nationality')}</Label>
                  <Input
                    id="nationality"
                    value={formData.nationality}
                    onChange={(e) => setFormData(prev => ({ ...prev, nationality: e.target.value }))}
                    required
                    dir={dir}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('dashboard.coordinator.form.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder={t('dashboard.coordinator.form.emailPlaceholder')}
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">{t('dashboard.coordinator.form.phoneNumber')} *</Label>
                  <Input
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    placeholder="05XXXXXXXX"
                    required
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="relativeMobileNumber">{t('dashboard.coordinator.form.relativeMobileNumber')}</Label>
                  <Input
                    id="relativeMobileNumber"
                    value={formData.relativeMobileNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, relativeMobileNumber: e.target.value }))}
                    placeholder="05XXXXXXXX"
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">{t('dashboard.coordinator.form.city')}</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder={t('dashboard.coordinator.form.cityPlaceholder')}
                    dir={dir}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="neighborhood">{t('dashboard.coordinator.form.neighborhood')}</Label>
                  <Input
                    id="neighborhood"
                    value={formData.neighborhood}
                    onChange={(e) => setFormData(prev => ({ ...prev, neighborhood: e.target.value }))}
                    placeholder={t('dashboard.coordinator.form.neighborhoodPlaceholder')}
                    dir={dir}
                  />
                </div>

              </div>
              )}

              {addFormStep === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">{t('dashboard.coordinator.form.gender')}</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value: TreatmentGender) => setFormData(prev => ({ ...prev, gender: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{t('dashboard.coordinator.form.genders.male')}</SelectItem>
                      <SelectItem value="female">{t('dashboard.coordinator.form.genders.female')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maritalStatus">{t('dashboard.coordinator.form.maritalStatus')}</Label>
                  <Select
                    value={formData.maritalStatus}
                    onValueChange={(value: TreatmentMaritalStatus) => setFormData(prev => ({ ...prev, maritalStatus: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">{t('dashboard.coordinator.form.maritalStatuses.single')}</SelectItem>
                      <SelectItem value="married">{t('dashboard.coordinator.form.maritalStatuses.married')}</SelectItem>
                      <SelectItem value="divorced">{t('dashboard.coordinator.form.maritalStatuses.divorced')}</SelectItem>
                      <SelectItem value="widowed">{t('dashboard.coordinator.form.maritalStatuses.widowed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="familyMembersCount">{t('dashboard.coordinator.form.familyMembersCount')}</Label>
                  <Input
                    id="familyMembersCount"
                    type="number"
                    min="0"
                    value={formData.familyMembersCount}
                    onChange={(e) => setFormData(prev => ({ ...prev, familyMembersCount: e.target.value }))}
                    required
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('dashboard.coordinator.form.educationLevel')}</Label>
                  <Select
                    value={formData.educationLevel}
                    onValueChange={(value: TreatmentEducationLevel) => setFormData(prev => ({ ...prev, educationLevel: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('dashboard.coordinator.form.selectEducationLevel')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(['illiterate', 'elementary', 'intermediate', 'high_school', 'diploma', 'bachelors', 'masters', 'doctorate'] as TreatmentEducationLevel[]).map((level) => (
                        <SelectItem key={level} value={level}>
                          {t(`dashboard.coordinator.form.educationLevels.${level}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('dashboard.coordinator.form.workSector')}</Label>
                  <Select
                    value={formData.workSector}
                    onValueChange={(value: TreatmentWorkSector) => setFormData(prev => ({ ...prev, workSector: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('dashboard.coordinator.form.selectWorkSector')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(['government', 'private', 'military', 'retired', 'unemployed', 'student', 'other'] as TreatmentWorkSector[]).map((sector) => (
                        <SelectItem key={sector} value={sector}>
                          {t(`dashboard.coordinator.form.workSectors.${sector}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profession">{t('dashboard.coordinator.form.profession')}</Label>
                  <Input
                    id="profession"
                    value={formData.profession}
                    onChange={(e) => setFormData(prev => ({ ...prev, profession: e.target.value }))}
                    placeholder={t('dashboard.coordinator.form.professionPlaceholder')}
                    dir={dir}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="monthlyIncome">{t('dashboard.coordinator.form.monthlyIncome')}</Label>
                  <Input
                    id="monthlyIncome"
                    type="number"
                    min="0"
                    value={formData.monthlyIncome}
                    onChange={(e) => setFormData(prev => ({ ...prev, monthlyIncome: e.target.value }))}
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('dashboard.coordinator.form.housingType')}</Label>
                  <Select
                    value={formData.housingType}
                    onValueChange={(value: TreatmentHousingType) => setFormData(prev => ({ ...prev, housingType: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('dashboard.coordinator.form.selectHousingType')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(['owned', 'rented', 'provided', 'other'] as TreatmentHousingType[]).map((type) => (
                        <SelectItem key={type} value={type}>
                          {t(`dashboard.coordinator.form.housingTypes.${type}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.housingType === 'rented' && (
                  <div className="space-y-2">
                    <Label htmlFor="rentAmount">{t('dashboard.coordinator.form.rentAmount')}</Label>
                    <Input
                      id="rentAmount"
                      type="number"
                      min="0"
                      value={formData.rentAmount}
                      onChange={(e) => setFormData(prev => ({ ...prev, rentAmount: e.target.value }))}
                      dir="ltr"
                    />
                  </div>
                )}
              </div>
              )}

              {addFormStep === 4 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="medicalCondition">{t('dashboard.coordinator.form.condition')}</Label>
                  <Textarea
                    id="medicalCondition"
                    value={formData.medicalCondition}
                    onChange={(e) => setFormData(prev => ({ ...prev, medicalCondition: e.target.value }))}
                    required
                    dir={dir}
                    rows={5}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="requiredTreatment">{t('dashboard.coordinator.form.treatment')}</Label>
                  <Textarea
                    id="requiredTreatment"
                    value={formData.requiredTreatment}
                    onChange={(e) => setFormData(prev => ({ ...prev, requiredTreatment: e.target.value }))}
                    dir={dir}
                    rows={5}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cost">{t('dashboard.coordinator.form.cost')}</Label>
                  <Input
                    id="cost"
                    type="number"
                    min="0"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                    required
                    dir="ltr"
                  />
                </div>

                {/* Medical Reports */}
                <div className="space-y-2">
                  <Label>{t('dashboard.coordinator.form.medicalReports')}</Label>
                  <MedicalReportUploader
                    reports={pendingReports}
                    onReportsChange={setPendingReports}
                    translationPrefix="dashboard.coordinator.form"
                  />
                </div>
              </div>
              )}

              {addFormStep === 5 && (
              <div className="space-y-4">
                {/* Is Case Critical */}
                <div className="flex items-center justify-between">
                  <Label className="flex-shrink-0">{t('dashboard.coordinator.form.isCritical')}</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="coord-isCritical"
                        checked={formData.isCritical === true}
                        onChange={() => setFormData(prev => ({ ...prev, isCritical: true }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.yes')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="coord-isCritical"
                        checked={formData.isCritical === false}
                        onChange={() => setFormData(prev => ({ ...prev, isCritical: false }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.no')}</span>
                    </label>
                  </div>
                </div>

                {/* Is Patient Hospitalized */}
                <div className="flex items-center justify-between">
                  <Label className="flex-shrink-0">{t('dashboard.coordinator.form.isHospitalized')}</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="coord-isHospitalized"
                        checked={formData.isHospitalized === true}
                        onChange={() => setFormData(prev => ({ ...prev, isHospitalized: true }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.yes')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="coord-isHospitalized"
                        checked={formData.isHospitalized === false}
                        onChange={() => setFormData(prev => ({ ...prev, isHospitalized: false }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.no')}</span>
                    </label>
                  </div>
                </div>

                {/* Has Insurance */}
                <div className="flex items-center justify-between">
                  <Label className="flex-shrink-0">{t('dashboard.coordinator.form.hasInsurance')}</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="coord-hasInsurance"
                        checked={formData.hasInsurance === true}
                        onChange={() => setFormData(prev => ({ ...prev, hasInsurance: true }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.yes')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="coord-hasInsurance"
                        checked={formData.hasInsurance === false}
                        onChange={() => setFormData(prev => ({ ...prev, hasInsurance: false, insuranceProvider: '' }))}
                        className="w-4 h-4"
                      />
                      <span>{t('common.no')}</span>
                    </label>
                  </div>
                </div>

                {/* Insurance Provider & Category (conditional) */}
                {formData.hasInsurance && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="coord-insuranceProvider">{t('dashboard.coordinator.form.insuranceProvider')}</Label>
                      <Input
                        id="coord-insuranceProvider"
                        value={formData.insuranceProvider}
                        onChange={(e) => setFormData(prev => ({ ...prev, insuranceProvider: e.target.value }))}
                        required
                        dir={dir}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coord-insuranceCategory">{t('dashboard.coordinator.form.insuranceCategory')}</Label>
                      <Input
                        id="coord-insuranceCategory"
                        value={formData.insuranceCategory}
                        onChange={(e) => setFormData(prev => ({ ...prev, insuranceCategory: e.target.value }))}
                        required
                        dir={dir}
                      />
                    </div>
                  </>
                )}

                {/* Category */}
                <div className="space-y-2">
                  <Label>{t('dashboard.coordinator.form.category')}</Label>
                  <Select
                    value={formData.categoryId}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, categoryId: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('dashboard.coordinator.form.selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {dir === 'rtl' ? (category.nameAr || category.name) : category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="coordinatorNotes">{t('dashboard.coordinator.form.notes')}</Label>
                  <Textarea
                    id="coordinatorNotes"
                    value={formData.coordinatorNotes}
                    onChange={(e) => setFormData(prev => ({ ...prev, coordinatorNotes: e.target.value }))}
                    dir={dir}
                    rows={6}
                  />
                </div>
              </div>
              )}

            </div>

            <div className="flex justify-between gap-2 px-6 py-4 border-t shrink-0 bg-gray-50">
              {addFormStep > 1 ? (
                <Button type="button" variant="ghost" onClick={() => setAddFormStep(s => s - 1)}>
                  {t('common.previous')}
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                {addFormStep < ADD_TOTAL_STEPS ? (
                  <Button type="button" onClick={() => setAddFormStep(s => s + 1)}>
                    {t('common.next')}
                  </Button>
                ) : (
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {t('dashboard.coordinator.form.submit')}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Case Modal */}
      <Dialog open={isViewOpen} onOpenChange={(open) => { setIsViewOpen(open); if (!open) { setPreviewUrl(null); setIsEditingDetails(false); setPendingReports([]); } }}>
        <DialogContent className="w-[95vw] max-w-7xl max-h-[99vh]" dir={dir}>
          <DialogHeader dir={dir}>
            <DialogTitle className="flex items-center gap-3 flex-wrap">
              {selectedRequest && (
                <>
                  <span>{dir === 'rtl' ? (selectedRequest.nameArabic || selectedRequest.applicantName) : (selectedRequest.nameEnglish || selectedRequest.applicantName)}</span>
                  <span className="text-muted-foreground font-normal text-sm" dir="ltr">{selectedRequest.nationalId || selectedRequest.idNumber}</span>
                  <Badge className={STATUS_COLORS[selectedRequest.status]}>
                    {t(`dashboard.coordinator.statuses.${selectedRequest.status}`)}
                  </Badge>
                  {selectedRequest.status === 'approved_treatment' && (
                    <Button
                      type="button"
                      size="sm"
                      className="ms-auto me-8"
                      disabled={!canStartTreatment(selectedRequest).ok}
                      title={canStartTreatment(selectedRequest).ok ? '' : t('dashboard.coordinator.startTreatmentGateTooltip')}
                      onClick={() => handleStartTreatment(selectedRequest)}
                    >
                      {t('dashboard.coordinator.startTreatmentButton')}
                    </Button>
                  )}
                  {selectedRequest.status === 'treatment_started' && (
                    <Button
                      type="button"
                      size="sm"
                      className="ms-auto me-8"
                      onClick={() => handleMarkCompleted(selectedRequest)}
                    >
                      {t('dashboard.coordinator.markCompletedButton')}
                    </Button>
                  )}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <Tabs defaultValue="details" className="w-full" dir={dir} onValueChange={(tab) => {
              if (tab === 'reports') {
                const firstReport = selectedRequest.medicalReports?.[0]?.url || selectedRequest.medicalReportUrl || null;
                setPreviewUrl(firstReport);
              } else if (tab === 'progressReports') {
                setPreviewUrl(selectedRequest.progressReports?.[0]?.url || null);
              } else if (tab === 'invoices') {
                const firstInvoice = selectedRequest.invoices?.[0]?.url || selectedRequest.invoiceUrls?.[0] || null;
                setPreviewUrl(firstInvoice);
              } else if (tab === 'paymentSlips') {
                setPreviewUrl(selectedRequest.paymentSlipUrls?.[0] || null);
              } else {
                setPreviewUrl(null);
              }
            }}>
              <div className="overflow-x-auto pb-2 scrollbar-thin">
              <TabsList className="inline-flex w-max md:w-full mb-4">
                <TabsTrigger value="details" className="md:flex-1">{t('dashboard.coordinator.tabs.details')}</TabsTrigger>
                <TabsTrigger value="reports" className="md:flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.reports')}
                  {(selectedRequest.medicalReports?.length || 0) > 0 && (
                    <Badge variant="secondary" className="text-xs">{selectedRequest.medicalReports?.length}</Badge>
                  )}
                </TabsTrigger>
                {['approved', 'approved_treatment', 'treatment_started', 'pending_invoices', 'completed'].includes(selectedRequest.status) && (
                  <TabsTrigger value="donations" className="md:flex-1 gap-2">
                    {t('dashboard.coordinator.tabs.donations')}
                    {(selectedRequest.donationEntries?.length || 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedRequest.donationEntries?.length}</Badge>
                    )}
                  </TabsTrigger>
                )}
                {['approved_treatment', 'treatment_started', 'pending_invoices', 'completed'].includes(selectedRequest.status) && (
                  <TabsTrigger value="progressReports" className="md:flex-1 gap-2">
                    {t('dashboard.coordinator.tabs.progressReports')}
                    {(selectedRequest.progressReports?.length || 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedRequest.progressReports?.length}</Badge>
                    )}
                  </TabsTrigger>
                )}
                {['approved_treatment', 'treatment_started', 'pending_invoices', 'completed'].includes(selectedRequest.status) && (
                  <TabsTrigger value="invoices" className="md:flex-1 gap-2">
                    {t('dashboard.coordinator.tabs.invoices')}
                    {(selectedRequest.invoices?.length || 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedRequest.invoices?.length}</Badge>
                    )}
                  </TabsTrigger>
                )}
                {selectedRequest.status === 'pending_invoices' && (
                  <TabsTrigger value="paymentSlips" className="md:flex-1 gap-2">
                    {t('dashboard.coordinator.tabs.paymentSlips')}
                    {(selectedRequest.paymentSlipUrls?.length || 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedRequest.paymentSlipUrls?.length}</Badge>
                    )}
                  </TabsTrigger>
                )}
                <TabsTrigger value="updates" className="md:flex-1 gap-2">
                  {t('dashboard.coordinator.tabs.updates')}
                  {(selectedRequest.updates?.length || 0) > 0 && (
                    <Badge variant="secondary" className="text-xs">{selectedRequest.updates?.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              </div>
              
              <TabsContent value="details" className="max-h-[72vh] flex flex-col">
                {/* Edit Toggle Button */}
                <div className="flex justify-end mb-4 shrink-0">
                  {selectedRequest.status !== 'completed' && !isEditingDetails && (
                    <Button variant="outline" onClick={startEditingDetails}>
                      <Edit className="h-4 w-4 me-2" />
                      {t('dashboard.coordinator.edit')}
                    </Button>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto">

                {isEditingDetails ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    {/* Card 1 - Personal Info */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.personalInfo')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.nameArabic')}</Label>
                        <Input
                          value={editFormData.nameArabic}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, nameArabic: e.target.value }))}
                          dir="rtl"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.nameEnglish')}</Label>
                        <Input
                          value={editFormData.nameEnglish}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, nameEnglish: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.idType')}</Label>
                        <Select
                          value={editFormData.idType}
                          onValueChange={(value: TreatmentIdType) => setEditFormData(prev => ({ ...prev, idType: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="national_id">{t('dashboard.coordinator.form.idTypes.nationalId')}</SelectItem>
                            <SelectItem value="residency">{t('dashboard.coordinator.form.idTypes.residency')}</SelectItem>
                            <SelectItem value="umrah_visit_visa">{t('dashboard.coordinator.form.idTypes.umrahVisitVisa')}</SelectItem>
                            <SelectItem value="passport">{t('dashboard.coordinator.form.idTypes.passport')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.idNumber')} *</Label>
                        <Input
                          value={editFormData.idNumber}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, idNumber: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <HijriDatePicker label={t('dashboard.coordinator.form.dateOfBirth')} labelClassName="text-xs font-medium leading-none" value={editFormData.dateOfBirth} onChange={(v) => setEditFormData(prev => ({ ...prev, dateOfBirth: v }))} />
                      </div>
                      <div className="space-y-1">
                        <HijriDatePicker label={t('dashboard.coordinator.form.idExpiryDate')} labelClassName="text-xs font-medium leading-none" value={editFormData.idExpiryDate} onChange={(v) => setEditFormData(prev => ({ ...prev, idExpiryDate: v }))} />
                      </div>
                      
                      {/* ID Document Upload */}
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.idDocument')}</Label>
                        {selectedRequest.idDocumentUrl ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setIdPreviewUrl(selectedRequest.idDocumentUrl!)}
                              className="block w-16 h-12 border rounded-lg overflow-hidden bg-muted hover:opacity-80 hover:ring-2 hover:ring-primary transition-all flex-shrink-0 flex items-center justify-center cursor-pointer"
                            >
                              {isPdfUrl(selectedRequest.idDocumentUrl) ? (
                                <div className="flex flex-col items-center gap-0.5">
                                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/></svg>
                                  <span className="text-[8px] font-bold text-red-500">PDF</span>
                                </div>
                              ) : (
                                <img src={selectedRequest.idDocumentUrl} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              )}
                            </button>
                            <label className="cursor-pointer flex-1">
                              <input
                                type="file"
                                accept="image/*,.pdf"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleUploadIdDocument(file);
                                }}
                                disabled={isSubmitting}
                              />
                              <Button variant="outline" size="sm" className="w-full h-10" asChild disabled={isSubmitting}>
                                <span className="flex items-center gap-1">
                                  {isSubmitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                                  {t('dashboard.coordinator.form.uploadIdDocument')}
                                </span>
                              </Button>
                            </label>
                          </div>
                        ) : (
                          <label className="cursor-pointer block">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUploadIdDocument(file);
                              }}
                              disabled={isSubmitting}
                            />
                            <div className="flex items-center justify-center gap-2 h-10 border-2 border-dashed rounded-md hover:border-primary transition-colors">
                              {isSubmitting ? (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              ) : (
                                <Upload className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="text-xs text-muted-foreground">
                                {t('dashboard.coordinator.form.uploadIdDocument')}
                              </span>
                            </div>
                          </label>
                        )}
                      </div>
                      </div>
                    </div>

                    {/* Card 2 - Contact & Demographics */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.contact')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.nationality')}</Label>
                        <Input
                          value={editFormData.nationality}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, nationality: e.target.value }))}
                          dir={dir}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.email')}</Label>
                        <Input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.phoneNumber')} *</Label>
                        <Input
                          value={editFormData.phoneNumber}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                          placeholder="05XXXXXXXX"
                          required
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.relativeMobileNumber')}</Label>
                        <Input
                          value={editFormData.relativeMobileNumber}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, relativeMobileNumber: e.target.value }))}
                          placeholder="05XXXXXXXX"
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.city')}</Label>
                        <Input
                          value={editFormData.city}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, city: e.target.value }))}
                          dir={dir}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.neighborhood')}</Label>
                        <Input
                          value={editFormData.neighborhood}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, neighborhood: e.target.value }))}
                          dir={dir}
                        />
                      </div>
                      </div>
                    </div>

                    {/* Card 3 - Socioeconomic */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.socioeconomic')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.gender')}</Label>
                        <Select
                          value={editFormData.gender}
                          onValueChange={(value: TreatmentGender) => setEditFormData(prev => ({ ...prev, gender: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">{t('dashboard.coordinator.form.genders.male')}</SelectItem>
                            <SelectItem value="female">{t('dashboard.coordinator.form.genders.female')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.maritalStatus')}</Label>
                        <Select
                          value={editFormData.maritalStatus}
                          onValueChange={(value: TreatmentMaritalStatus) => setEditFormData(prev => ({ ...prev, maritalStatus: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="single">{t('dashboard.coordinator.form.maritalStatuses.single')}</SelectItem>
                            <SelectItem value="married">{t('dashboard.coordinator.form.maritalStatuses.married')}</SelectItem>
                            <SelectItem value="divorced">{t('dashboard.coordinator.form.maritalStatuses.divorced')}</SelectItem>
                            <SelectItem value="widowed">{t('dashboard.coordinator.form.maritalStatuses.widowed')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.familyMembersCount')}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editFormData.familyMembersCount}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, familyMembersCount: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.educationLevel')}</Label>
                        <Select value={editFormData.educationLevel} onValueChange={(value: TreatmentEducationLevel) => setEditFormData(prev => ({ ...prev, educationLevel: value }))}>
                          <SelectTrigger><SelectValue placeholder={t('dashboard.coordinator.form.selectEducationLevel')} /></SelectTrigger>
                          <SelectContent>
                            {(['illiterate', 'elementary', 'intermediate', 'high_school', 'diploma', 'bachelors', 'masters', 'doctorate'] as TreatmentEducationLevel[]).map((level) => (
                              <SelectItem key={level} value={level}>{t(`dashboard.coordinator.form.educationLevels.${level}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.workSector')}</Label>
                        <Select value={editFormData.workSector} onValueChange={(value: TreatmentWorkSector) => setEditFormData(prev => ({ ...prev, workSector: value }))}>
                          <SelectTrigger><SelectValue placeholder={t('dashboard.coordinator.form.selectWorkSector')} /></SelectTrigger>
                          <SelectContent>
                            {(['government', 'private', 'military', 'retired', 'unemployed', 'student', 'other'] as TreatmentWorkSector[]).map((sector) => (
                              <SelectItem key={sector} value={sector}>{t(`dashboard.coordinator.form.workSectors.${sector}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.profession')}</Label>
                        <Input value={editFormData.profession} onChange={(e) => setEditFormData(prev => ({ ...prev, profession: e.target.value }))} dir={dir} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.monthlyIncome')}</Label>
                        <Input type="number" min="0" value={editFormData.monthlyIncome} onChange={(e) => setEditFormData(prev => ({ ...prev, monthlyIncome: e.target.value }))} dir="ltr" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.housingType')}</Label>
                        <Select value={editFormData.housingType} onValueChange={(value: TreatmentHousingType) => setEditFormData(prev => ({ ...prev, housingType: value }))}>
                          <SelectTrigger><SelectValue placeholder={t('dashboard.coordinator.form.selectHousingType')} /></SelectTrigger>
                          <SelectContent>
                            {(['owned', 'rented', 'provided', 'other'] as TreatmentHousingType[]).map((type) => (
                              <SelectItem key={type} value={type}>{t(`dashboard.coordinator.form.housingTypes.${type}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {editFormData.housingType === 'rented' && (
                        <div className="space-y-1">
                          <Label className="text-xs">{t('dashboard.coordinator.form.rentAmount')}</Label>
                          <Input type="number" min="0" value={editFormData.rentAmount} onChange={(e) => setEditFormData(prev => ({ ...prev, rentAmount: e.target.value }))} dir="ltr" />
                        </div>
                      )}
                      </div>
                    </div>

                    {/* Card 4 - Medical Info */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.medical')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.condition')}</Label>
                        <Textarea
                          value={editFormData.medicalCondition}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, medicalCondition: e.target.value }))}
                          dir={dir}
                          rows={4}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.treatment')}</Label>
                        <Textarea
                          value={editFormData.requiredTreatment}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, requiredTreatment: e.target.value }))}
                          dir={dir}
                          rows={4}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.cost')}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editFormData.cost}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, cost: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      </div>
                    </div>

                    {/* Card 5 - Case Classifications */}
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t('common.stepLabels.caseStatus')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex-shrink-0">{t('dashboard.coordinator.form.isCritical')}</Label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="edit-isCritical"
                              checked={editFormData.isCritical === true}
                              onChange={() => setEditFormData(prev => ({ ...prev, isCritical: true }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.yes')}</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="edit-isCritical"
                              checked={editFormData.isCritical === false}
                              onChange={() => setEditFormData(prev => ({ ...prev, isCritical: false }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.no')}</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex-shrink-0">{t('dashboard.coordinator.form.isHospitalized')}</Label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="edit-isHospitalized"
                              checked={editFormData.isHospitalized === true}
                              onChange={() => setEditFormData(prev => ({ ...prev, isHospitalized: true }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.yes')}</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="edit-isHospitalized"
                              checked={editFormData.isHospitalized === false}
                              onChange={() => setEditFormData(prev => ({ ...prev, isHospitalized: false }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.no')}</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex-shrink-0">{t('dashboard.coordinator.form.hasInsurance')}</Label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="edit-hasInsurance"
                              checked={editFormData.hasInsurance === true}
                              onChange={() => setEditFormData(prev => ({ ...prev, hasInsurance: true }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.yes')}</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="edit-hasInsurance"
                              checked={editFormData.hasInsurance === false}
                              onChange={() => setEditFormData(prev => ({ ...prev, hasInsurance: false, insuranceProvider: '', insuranceCategory: '' }))}
                              className="w-4 h-4"
                            />
                            <span>{t('common.no')}</span>
                          </label>
                        </div>
                      </div>
                      {editFormData.hasInsurance && (
                        <>
                          <div className="space-y-1">
                            <Label className="text-xs">{t('dashboard.coordinator.form.insuranceProvider')}</Label>
                            <Input
                              value={editFormData.insuranceProvider}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, insuranceProvider: e.target.value }))}
                              dir={dir}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">{t('dashboard.coordinator.form.insuranceCategory')}</Label>
                            <Input
                              value={editFormData.insuranceCategory}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, insuranceCategory: e.target.value }))}
                              dir={dir}
                            />
                          </div>
                        </>
                      )}
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.category')}</Label>
                        <Select
                          value={editFormData.categoryId}
                          onValueChange={(value) => setEditFormData(prev => ({ ...prev, categoryId: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('dashboard.coordinator.form.selectCategory')} />
                          </SelectTrigger>
                          <SelectContent>
                            {activeCategories.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {dir === 'rtl' ? (category.nameAr || category.name) : category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('dashboard.coordinator.form.notes')}</Label>
                        <Textarea
                          value={editFormData.coordinatorNotes}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, coordinatorNotes: e.target.value }))}
                          dir={dir}
                          rows={4}
                        />
                      </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {/* Column 1 - Applicant Info */}
                    <div className="space-y-4">
                      <div className="p-4 border rounded-lg space-y-3">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.coordinator.applicantInfo')}
                        </h4>
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.form.nationalId')}</Label>
                          <p className="font-mono font-medium">{selectedRequest.nationalId}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.form.age')}</Label>
                          <p className="font-medium">{selectedRequest.dateOfBirth ? Math.floor((Date.now() - new Date(selectedRequest.dateOfBirth).getTime()) / 31557600000) : (selectedRequest.age || '-')} {t('common.years')}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.form.applicantName')}</Label>
                          <p className="font-medium">{selectedRequest.applicantName}</p>
                        </div>
                        {selectedRequest.phoneNumber && (
                          <div>
                            <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.form.phoneNumber')}</Label>
                            <p className="font-medium font-mono" dir="ltr">{selectedRequest.phoneNumber}</p>
                          </div>
                        )}
                        
                        {/* ID Document (View Only) */}
                        {selectedRequest.idDocumentUrl && (
                          <div className="pt-2 border-t">
                            <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.form.idDocument')}</Label>
                            <div className="mt-2 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setIdPreviewUrl(selectedRequest.idDocumentUrl!)}
                                className="w-20 h-14 border rounded-lg overflow-hidden bg-muted hover:opacity-80 hover:ring-2 hover:ring-primary transition-all flex-shrink-0 flex items-center justify-center cursor-pointer"
                              >
                                {isPdfUrl(selectedRequest.idDocumentUrl) ? (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/></svg>
                                    <span className="text-[9px] font-bold text-red-500">PDF</span>
                                  </div>
                                ) : (
                                  <img src={selectedRequest.idDocumentUrl} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                )}
                              </button>
                              <span className="text-sm truncate max-w-[150px] text-muted-foreground">{selectedRequest.idDocumentName || 'ID Document'}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Column 2 - Medical Info */}
                    <div className="space-y-4">
                      <div className="p-4 border rounded-lg space-y-3">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.coordinator.medicalInfo')}
                        </h4>
                        {(selectedRequest.categoryId || selectedRequest.categoryName) && (
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(selectedRequest.categoryId)}`}>
                              {getCategoryLabel(selectedRequest)}
                            </span>
                          </div>
                        )}
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.form.condition')}</Label>
                          <p className="font-medium">{selectedRequest.medicalCondition}</p>
                        </div>
                        {selectedRequest.requiredTreatment && (
                          <div>
                            <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.form.treatment')}</Label>
                            <p className="font-medium">{selectedRequest.requiredTreatment}</p>
                          </div>
                        )}
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.form.cost')}</Label>
                          <p className="font-medium text-lg inline-flex items-center gap-1">{getNetMedicalCost(selectedRequest).toLocaleString('en-US')} <SaudiRiyalIcon className="h-5 w-5" /></p>
                        </div>
                        {selectedRequest.approvedAmount !== undefined && (
                          <div>
                            <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.approvedAmount')}</Label>
                            <p className="font-medium text-lg text-green-600 inline-flex items-center gap-1">{selectedRequest.approvedAmount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-5 w-5" /></p>
                          </div>
                        )}
                      </div>

                    </div>

                    {/* Column 3 - Workflow Info */}
                    <div className="space-y-4">
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        {t('dashboard.coordinator.caseInfo')}
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.createdAt')}</Label>
                          <p className="font-medium text-sm">{formatLocalDate(new Date(selectedRequest.createdAt))}</p>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.createdBy')}</Label>
                          <p className="font-medium text-sm capitalize">
                            {translateRole(selectedRequest.createdBy || 'customer')}
                            {selectedRequest.submittedByName && selectedRequest.createdBy === 'customer' && (
                              <span className="text-muted-foreground"> ({selectedRequest.submittedByName})</span>
                            )}
                          </p>
                        </div>
                        {selectedRequest.reviewedAt && (
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.reviewedAt')}</Label>
                            <p className="font-medium text-sm">{formatLocalDate(new Date(selectedRequest.reviewedAt))}</p>
                          </div>
                        )}
                        {selectedRequest.adminDecisionAt && (
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">{t('dashboard.coordinator.decisionAt')}</Label>
                            <p className="font-medium text-sm">{formatLocalDate(new Date(selectedRequest.adminDecisionAt))}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Review Status */}
                    {(selectedRequest.socialStatusFits !== undefined || selectedRequest.medicalConditionFits !== undefined) && (
                      <div className="p-4 border rounded-lg space-y-3 bg-blue-50 dark:bg-blue-950">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.coordinator.reviewStatus')}
                        </h4>
                        <div className="space-y-2">
                          {selectedRequest.socialStatusFits !== undefined && (
                            <div className="flex items-center gap-2">
                              <Badge variant={selectedRequest.socialStatusFits ? 'default' : 'destructive'}>
                                {selectedRequest.socialStatusFits ? '✓' : '✗'}
                              </Badge>
                              <span className="text-sm">{t('dashboard.coordinator.form.review.socialStatusFits')}</span>
                            </div>
                          )}
                          {selectedRequest.medicalConditionFits !== undefined && (
                            <div className="flex items-center gap-2">
                              <Badge variant={selectedRequest.medicalConditionFits ? 'default' : 'destructive'}>
                                {selectedRequest.medicalConditionFits ? '✓' : '✗'}
                              </Badge>
                              <span className="text-sm">{t('dashboard.coordinator.form.review.medicalConditionFits')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Coordinator Notes */}
                    {selectedRequest.coordinatorNotes && (
                      <div className="p-4 border rounded-lg space-y-3">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.coordinator.form.notes')}
                        </h4>
                        <p className="text-sm">{selectedRequest.coordinatorNotes}</p>
                      </div>
                    )}

                    {/* Cost Summary Card */}
                    {((selectedRequest.cost || 0) > 0 || (selectedRequest.invoices && selectedRequest.invoices.length > 0)) && (
                      <div className="p-4 border rounded-lg space-y-3 bg-primary/5 border-primary/20">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.coordinator.costSummary')}
                        </h4>
                        <div className="space-y-2">
                          {selectedRequest.invoices && selectedRequest.invoices.length > 0 && (
                            <>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">{t('dashboard.coordinator.totalInvoices')}</span>
                                <span className="font-bold text-primary inline-flex items-center gap-1">
                                  {selectedRequest.invoices.reduce((sum, inv) => sum + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" />
                                </span>
                              </div>
                              {selectedRequest.invoices.reduce((sum, inv) => sum + (inv.taxAmount || 0), 0) > 0 && (
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-muted-foreground">{t('dashboard.coordinator.totalVATInvoices')}</span>
                                  <span className="font-medium text-sm inline-flex items-center gap-1">
                                    {selectedRequest.invoices.reduce((sum, inv) => sum + (inv.taxAmount || 0), 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                                  </span>
                                </div>
                              )}
                              {(() => {
                                const expectedCb = (selectedRequest.invoices || []).reduce((sum, inv) => {
                                  const prov = activeProviders.find(p => p.id === inv.providerId);
                                  const pct = prov?.cashbackPercentage || 0;
                                  return sum + ((inv.totalAfterDiscount || 0) + (inv.taxAmount || 0)) * pct / 100;
                                }, 0);
                                return expectedCb > 0 ? (
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-purple-700">{t('dashboard.coordinator.cashbackExpected')}</span>
                                    <span className="font-medium text-sm text-purple-700 inline-flex items-center gap-1">
                                      {Math.round(expectedCb).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                                    </span>
                                  </div>
                                ) : null;
                              })()}
                            </>
                          )}
                          {!(selectedRequest.invoices && selectedRequest.invoices.length > 0) && (selectedRequest.cost || 0) > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-muted-foreground">{t('dashboard.coordinator.estimatedCost')}</span>
                              <span className="font-bold text-primary inline-flex items-center gap-1">
                                {(selectedRequest.cost || 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" />
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Admin Notes */}
                    {selectedRequest.adminNotes && (
                      <div className="p-4 border rounded-lg space-y-3 bg-purple-50 dark:bg-purple-950">
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {t('dashboard.coordinator.adminNotes')}
                        </h4>
                        <p className="text-sm">{selectedRequest.adminNotes}</p>
                      </div>
                    )}
                  </div>
                  </div>
                )}
                </div>
                {isEditingDetails && selectedRequest.status !== 'completed' && (
                  <div className="flex justify-end gap-2 pt-4 border-t mt-4 shrink-0 bg-white -mx-2 px-2 py-3 rounded-b-lg">
                    <Button variant="outline" onClick={() => setIsEditingDetails(false)} disabled={isSubmitting}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={saveEditedDetails} disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                      {t('common.save')}
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="reports" className="h-[72vh]">
                <div className="flex flex-col md:flex-row gap-4 h-full">
                  {/* Reports List */}
                  <div className="w-full md:w-96 flex-shrink-0 border rounded-lg p-3 space-y-2 overflow-y-auto flex flex-col max-h-[30vh] md:max-h-none">
                    {/* Header with Add Report Button */}
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        {t('dashboard.coordinator.medicalReports')}
                      </h4>
                      {selectedRequest.status !== 'completed' && (
                        <MedicalReportUploader
                          reports={pendingReports}
                          onReportsChange={setPendingReports}
                          translationPrefix="dashboard.coordinator"
                        />
                      )}
                    </div>
                    
                    {/* Reports with details */}
                    <div className="flex-1 space-y-2 overflow-y-auto">
                      {selectedRequest.medicalReports?.map((report, i) => (
                        <div
                          key={`rep-${i}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === report.url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                          onClick={() => setPreviewUrl(report.url)}
                        >
                          <div className="flex gap-3">
                            <div className="min-w-0 flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                <span className="font-medium text-sm truncate">{report.name}</span>
                              </div>
                              {report.providerName && (
                                <p className={`text-sm truncate ${previewUrl === report.url ? 'text-[var(--namat-green)]' : 'opacity-70'}`}>{report.providerName}</p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={report.url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); setEditingFileIndex(i); setEditingFileType('report'); }}
                              >
                                <Edit className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Legacy reports (single URL) */}
                      {selectedRequest.medicalReportUrl && !selectedRequest.medicalReports?.length && (
                        <div
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === selectedRequest.medicalReportUrl ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                          onClick={() => setPreviewUrl(selectedRequest.medicalReportUrl!)}
                        >
                          <div className="flex gap-3">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <FileText className="h-4 w-4 flex-shrink-0" />
                              <span className="font-medium text-sm">{t('dashboard.coordinator.viewReport')}</span>
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={selectedRequest.medicalReportUrl!}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!selectedRequest.medicalReports?.length && !selectedRequest.medicalReportUrl && (
                        <p className="text-sm text-muted-foreground">{t('dashboard.coordinator.noReports')}</p>
                      )}
                    </div>
                    
                    {/* Upload Pending Reports Button */}
                    {pendingReports.length > 0 && selectedRequest.status !== 'completed' && (
                      <div className="border-t mt-3 pt-3 space-y-2">
                        <Button
                          size="sm"
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={handleAddReportFromDetails}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                          {t('dashboard.coordinator.uploadReport')} ({pendingReports.length})
                        </Button>
                        {/* Pending Reports Thumbnails */}
                        <div className="flex flex-wrap gap-2">
                          {pendingReports.map((report, index) => (
                            <div key={index} className="relative group">
                              <div className="w-12 h-12 rounded-lg border overflow-hidden bg-muted">
                                {report.thumbnailUrl ? (
                                  report.file.type === 'application/pdf' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-muted">
                                      <FileText className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                  ) : (
                                    <img src={report.thumbnailUrl} alt={report.file.name} className="w-full h-full object-cover" />
                                  )
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-muted">
                                    <FileText className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute -top-1 -right-1 h-4 w-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => setPendingReports(prev => prev.filter((_, i) => i !== index))}
                              >
                                <X className="h-2.5 w-2.5" />
                              </Button>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-popover text-popover-foreground text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                <div className="font-medium truncate max-w-32">{report.file.name}</div>
                                <div className="text-muted-foreground">{report.providerName}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Preview Area */}
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-800 dark:bg-gray-900">
                    {previewUrl ? (
                      <>
                        {isImageUrl(previewUrl) ? (
                          <div className="w-full h-full overflow-auto">
                            <img
                              src={previewUrl}
                              alt="Document Preview"
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <iframe
                            src={previewUrl}
                            className="w-full h-full bg-gray-800"
                            title="Document Preview"
                            style={{ colorScheme: 'dark' }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t('dashboard.coordinator.selectReport')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
              
              {/* Donations Tab */}
              <TabsContent value="donations" className="h-[72vh] overflow-y-auto">
                <div className="space-y-6">
                  <div className="flex flex-wrap gap-3">
                    <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-amber-50 border border-amber-200">
                      <span className="text-sm text-amber-700">{t('dashboard.coordinator.caseCost')}</span>
                      <span className="font-semibold text-amber-900 inline-flex items-center gap-1" dir="ltr">
                        {getCaseRemainingBudget(selectedRequest.id).caseCost.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                      </span>
                    </div>
                    {(() => {
                      const totalFunded = selectedRequest.donationEntries?.reduce((sum: number, e: any) => sum + (e.amount || 0), 0) || 0;
                      const caseCost = getCaseRemainingBudget(selectedRequest.id).caseCost;
                      const coveragePct = caseCost > 0 ? Math.round((totalFunded / caseCost) * 100) : 0;
                      // Expected cashback (مستحق) from provider percentages
                      const expectedCashback = (selectedRequest.invoices || []).reduce((sum, inv) => {
                        const prov = activeProviders.find(p => p.id === inv.providerId);
                        const pct = prov?.cashbackPercentage || 0;
                        return sum + ((inv.totalAfterDiscount || 0) + (inv.taxAmount || 0)) * pct / 100;
                      }, 0);
                      return (
                        <>
                          <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-blue-50 border border-blue-200">
                            <span className="text-sm text-blue-700">{t('dashboard.coordinator.totalFunded')}</span>
                            <span className="font-semibold text-blue-900 inline-flex items-center gap-1" dir="ltr">
                              {totalFunded.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                            </span>
                          </div>
                          <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-emerald-50 border border-emerald-200">
                            <span className="text-sm text-emerald-700">{t('dashboard.coordinator.coverage')}</span>
                            <span className="font-semibold text-emerald-900">
                              {coveragePct}%
                            </span>
                          </div>
                          {expectedCashback > 0 && (
                            <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-purple-50 border border-purple-200">
                              <span className="text-sm text-purple-700">{t('dashboard.coordinator.cashbackExpected')}</span>
                              <span className="font-semibold text-purple-900 inline-flex items-center gap-1" dir="ltr">
                                {Math.round(expectedCashback).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {/* Existing Donations List */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        {t('dashboard.coordinator.donationsList')}
                      </h4>
                      {['approved', 'approved_treatment', 'treatment_started', 'pending_invoices', 'completed'].includes(selectedRequest.status) && (
                        <Button
                          size="sm"
                          onClick={() => { setAllocateLinkKey(''); setAllocateAmount(''); setShowAllocateLink(true); }}
                        >
                          <Link2 className="h-4 w-4 me-2" />
                          {t('dashboard.coordinator.allocateLink')}
                        </Button>
                      )}
                    </div>
                    {selectedRequest.donationEntries && selectedRequest.donationEntries.length > 0 ? (
                      <div className="space-y-3">
                        {selectedRequest.donationEntries.map((entry, index) => (
                          <div key={index} className="p-4 border rounded-lg bg-card space-y-3">
                              <>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <Badge variant="secondary">
                                      {t(`dashboard.coordinator.platforms.${entry.platform}`)}
                                    </Badge>
                                    <span className="font-mono font-medium" dir="ltr">{entry.caseNumber}</span>
                                    {entry.linkTitle && (
                                      <span className="text-sm text-muted-foreground">{entry.linkTitle}</span>
                                    )}
                                    {entry.url && (
                                      <a
                                        href={entry.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline flex items-center gap-1 text-sm"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                        {t('dashboard.coordinator.form.donationUrl')}
                                      </a>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {(() => {
                                      const key = `${entry.platform}_${entry.caseNumber}`;
                                      const usage = linkUsageMap[key];
                                      if (!usage || usage.count <= 1) return null;
                                      const pct = usage.totalAllocated > 0 ? Math.round(((entry.amount || 0) / usage.totalAllocated) * 100) : 0;
                                      return (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 border border-purple-200 text-xs font-medium text-purple-700">
                                          {pct}% · <span dir="ltr" className="inline-flex items-center gap-0.5">{usage.totalAllocated.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" /></span> ({usage.count})
                                        </span>
                                      );
                                    })()}
                                    {coordCanDeleteAssignments && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveDonation(index)}
                                        disabled={isSubmitting}
                                        className="text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                {entry.amount !== undefined && entry.amount > 0 && (
                                  <div className="flex gap-6 text-sm">
                                    <div>
                                      <span className="text-muted-foreground">{t('dashboard.coordinator.form.amount')}: </span>
                                      <span className="font-medium inline-flex items-center gap-1" dir="ltr">{entry.amount.toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></span>
                                    </div>
                                  </div>
                                )}
                                {entry.addedAt && (
                                  <div className="text-xs text-muted-foreground">
                                    {formatLocalDate(new Date(entry.addedAt))}
                                  </div>
                                )}
                              </>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>{t('dashboard.coordinator.noDonations')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Allocate Link to Case Modal */}
              <Dialog open={showAllocateLink} onOpenChange={(open) => {
                setShowAllocateLink(open);
                if (!open) { setAllocateLinkKey(''); setAllocateAmount(''); setPendingAllocations([]); }
              }}>
                <DialogContent className="max-w-lg max-h-[85vh] flex flex-col overflow-hidden" dir={dir}>
                  <DialogHeader className="flex-shrink-0">
                    <DialogTitle>{t('dashboard.coordinator.allocateLink')}</DialogTitle>
                  </DialogHeader>
                  {(() => {
                    const caseEntryKeys = new Set(
                      (selectedRequest?.donationEntries || []).map(e => e.linkId)
                    );
                    const queuedKeys = new Set(pendingAllocations.map(p => p.linkKey));
                    const caseCategoryId = selectedRequest?.categoryId;
                    const availableLinks = !caseCategoryId
                      ? []
                      : allLinks.filter(l =>
                          l.remaining > 0 &&
                          !caseEntryKeys.has(l._key) &&
                          !queuedKeys.has(l._key) &&
                          l.treatmentCategoryId === caseCategoryId,
                        );
                    const selectedLink = allLinks.find(l => l._key === allocateLinkKey);
                    const caseBudget = selectedRequest ? getCaseRemainingBudget(selectedRequest.id) : { caseCost: 0, totalFunded: 0, remaining: 0 };
                    const queuedSum = pendingAllocations.reduce((s, p) => s + p.amount, 0);
                    const caseRemainingAfterQueue = caseBudget.remaining - queuedSum;
                    const maxAmount = selectedLink ? Math.min(selectedLink.remaining, caseRemainingAfterQueue) : 0;

                    return (
                      <>
                        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-4 px-1">
                          {/* Queued allocations */}
                          {pendingAllocations.length > 0 && (
                            <div className="space-y-2">
                              <Label>{t('dashboard.coordinator.donationsList')} ({pendingAllocations.length})</Label>
                              <div className="space-y-1.5">
                                {pendingAllocations.map((p, idx) => {
                                  const pl = allLinks.find(l => l._key === p.linkKey);
                                  return (
                                    <div key={p.linkKey} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-muted/50 border text-sm">
                                      <span className="truncate">
                                        {pl?.linkTitle ? `${pl.linkTitle} — ` : ''}{pl ? t(`dashboard.coordinator.platforms.${pl.platform}`) : ''} / {pl?.platformCaseNumber}
                                      </span>
                                      <span className="flex items-center gap-2 flex-shrink-0">
                                        <span className="font-semibold" dir="ltr">{p.amount.toLocaleString('en-US')} SAR</span>
                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setPendingAllocations(prev => prev.filter((_, i) => i !== idx))}>
                                          <X className="h-3.5 w-3.5" />
                                        </Button>
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {!caseCategoryId && (
                            <p className="text-sm text-muted-foreground py-4 text-center">
                              {t('dashboard.admin.helps.linksView.classifyCaseFirst')}
                            </p>
                          )}
                          {caseCategoryId && availableLinks.length === 0 && (
                            <p className="text-sm text-muted-foreground py-4 text-center">
                              {t('dashboard.admin.helps.linksView.noLinksForCategory')}
                            </p>
                          )}
                          <div className="space-y-2">
                            <Label>{t('dashboard.coordinator.selectLink')} *</Label>
                            {(() => {
                              const sel = allLinks.find(l => l._key === allocateLinkKey);
                              const selectLabel = sel
                                ? `${sel.linkTitle ? `${sel.linkTitle} — ` : ''}${t(`dashboard.coordinator.platforms.${sel.platform}`)} / ${sel.platformCaseNumber}`
                                : t('dashboard.coordinator.selectLinkPlaceholder');
                              return (
                                <Popover open={openAllocateLinkPicker} onOpenChange={setOpenAllocateLinkPicker}>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      aria-expanded={openAllocateLinkPicker}
                                      className="w-full justify-between h-10 font-normal"
                                      dir={dir}
                                    >
                                      <span className="truncate text-start">{selectLabel}</span>
                                      <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    className="w-[--radix-popover-trigger-width] p-0"
                                    align="start"
                                    dir={dir}
                                  >
                                    <Command
                                      filter={(value, search) => {
                                        return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                                      }}
                                    >
                                      <CommandInput placeholder={t('dashboard.coordinator.selectLinkPlaceholder')} dir={dir} />
                                      <CommandList>
                                        <CommandEmpty>{t('dashboard.coordinator.noDonations')}</CommandEmpty>
                                        <CommandGroup>
                                          {availableLinks.map((l) => {
                                            const label = `${l.linkTitle ? `${l.linkTitle} — ` : ''}${t(`dashboard.coordinator.platforms.${l.platform}`)} / ${l.platformCaseNumber}`;
                                            const haystack = `${label} ${l.platformCaseNumber} ${l.linkTitle || ''}`.toLowerCase();
                                            return (
                                              <CommandItem
                                                key={l._key}
                                                value={haystack}
                                                onSelect={() => {
                                                  setAllocateLinkKey(l._key);
                                                  const max = Math.min(l.remaining, caseRemainingAfterQueue);
                                                  setAllocateAmount(max > 0 ? String(max) : '');
                                                  setOpenAllocateLinkPicker(false);
                                                }}
                                              >
                                                <Check className={`me-2 h-4 w-4 ${allocateLinkKey === l._key ? 'opacity-100' : 'opacity-0'}`} />
                                                <div className="flex flex-col">
                                                  <span className="text-sm">{label}</span>
                                                  <span className="text-xs text-muted-foreground" dir="ltr">
                                                    {t('dashboard.coordinator.linkBudget.available')}: {l.remaining.toLocaleString('en-US')} SAR
                                                  </span>
                                                </div>
                                              </CommandItem>
                                            );
                                          })}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              );
                            })()}
                          </div>
                          {selectedLink && (
                            <>
                              <div className="px-3 py-2 rounded-md text-xs bg-muted/50 border space-y-1">
                                {selectedLink.linkTitle && <p className="font-medium text-sm">{selectedLink.linkTitle}</p>}
                                <p>{t('dashboard.coordinator.linkBudget.available')}: <span className="font-semibold" dir="ltr">{selectedLink.remaining.toLocaleString('en-US')}</span> SAR</p>
                                <p>{t('dashboard.coordinator.caseBudget.remaining')}: <span className="font-semibold" dir="ltr">{caseRemainingAfterQueue.toLocaleString('en-US')}</span> SAR</p>
                                {maxAmount > 0 && <p>{t('dashboard.coordinator.linkBudget.maxLabel')}: <span className="font-semibold" dir="ltr">{maxAmount.toLocaleString('en-US')}</span> SAR</p>}
                              </div>
                              <div className="space-y-2">
                                <Label>{t('dashboard.coordinator.form.amount')} *</Label>
                                <Input
                                  type="number"
                                  value={allocateAmount}
                                  onChange={(e) => setAllocateAmount(e.target.value)}
                                  dir="ltr"
                                  max={maxAmount}
                                  placeholder={t('dashboard.coordinator.form.amountPlaceholder')}
                                />
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full gap-1"
                                onClick={addToPendingAllocations}
                                disabled={!allocateLinkKey || !allocateAmount || Number(allocateAmount) <= 0}
                              >
                                <Plus className="h-4 w-4" />
                                {t('dashboard.coordinator.addDonationBtn')}
                              </Button>
                            </>
                          )}
                        </div>
                        <DialogFooter className="flex-shrink-0">
                          <Button variant="outline" onClick={() => { setShowAllocateLink(false); setAllocateLinkKey(''); setAllocateAmount(''); setPendingAllocations([]); }}>
                            {t('common.cancel')}
                          </Button>
                          <Button
                            onClick={handleAllocateLink}
                            disabled={isSubmitting || (pendingAllocations.length === 0 && (!allocateLinkKey || !allocateAmount || Number(allocateAmount) <= 0))}
                          >
                            {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Link2 className="h-4 w-4 me-2" />}
                            {t('dashboard.coordinator.allocateBtn')}
                            {(() => {
                              const total = pendingAllocations.length + (allocateLinkKey && Number(allocateAmount) > 0 ? 1 : 0);
                              return total > 1 ? ` (${total})` : '';
                            })()}
                          </Button>
                        </DialogFooter>
                      </>
                    );
                  })()}
                </DialogContent>
              </Dialog>

              {/* Invoices Tab */}
              <TabsContent value="progressReports" className="h-[72vh]">
                <div className="flex flex-col md:flex-row gap-4 h-full">
                  {/* Progress Reports List */}
                  <div className="w-full md:w-96 flex-shrink-0 border rounded-lg p-3 space-y-2 overflow-y-auto flex flex-col max-h-[30vh] md:max-h-none">
                    {/* Header with Add Report Button */}
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        {t('common.progressReports.title')}
                      </h4>
                      {['approved_treatment', 'treatment_started'].includes(selectedRequest.status) && (
                        <ProgressReportUploader
                          reports={[]}
                          pending={pendingProgressReports}
                          onPendingChange={setPendingProgressReports}
                          translationPrefix="common.progressReports"
                          hideList
                        />
                      )}
                    </div>

                    {/* Reports with details */}
                    <div className="flex-1 space-y-2 overflow-y-auto">
                      {selectedRequest.progressReports?.map((report, i) => (
                        <div
                          key={`prog-${i}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === report.url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                          onClick={() => setPreviewUrl(report.url)}
                        >
                          <div className="flex gap-3">
                            <div className="min-w-0 flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                <span className="font-medium text-sm truncate">{report.name}</span>
                              </div>
                              {report.providerName && (
                                <p className={`text-sm truncate ${previewUrl === report.url ? 'text-[var(--namat-green)]' : 'opacity-70'}`}>{(() => {
                                  const p = activeProviders.find(pr => pr.id === report.providerId);
                                  return language === 'ar'
                                    ? (p?.nameAr || report.providerName)
                                    : (report.providerName || p?.name);
                                })()}</p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={report.url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); setEditingFileIndex(i); setEditingFileType('progressReport'); }}
                              >
                                <Edit className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {!selectedRequest.progressReports?.length && (
                        <p className="text-sm text-muted-foreground">{t('common.progressReports.list.empty')}</p>
                      )}
                    </div>

                    {/* Upload Pending Reports Button */}
                    {pendingProgressReports.length > 0 && ['approved_treatment', 'treatment_started'].includes(selectedRequest.status) && (
                      <div className="border-t mt-3 pt-3 space-y-2">
                        <Button
                          size="sm"
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={handleUploadProgressReports}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                          {t('common.progressReports.addButton')} ({pendingProgressReports.length})
                        </Button>
                        {/* Pending Reports Thumbnails */}
                        <div className="flex flex-wrap gap-2">
                          {pendingProgressReports.map((report, index) => (
                            <div key={index} className="relative group">
                              <div className="w-12 h-12 rounded-lg border overflow-hidden bg-muted">
                                {report.thumbnailUrl && report.file && !report.file.type.includes('pdf') ? (
                                  <img src={report.thumbnailUrl} alt={report.file?.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-muted">
                                    <FileText className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute -top-1 -right-1 h-4 w-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => setPendingProgressReports(prev => prev.filter((_, i) => i !== index))}
                              >
                                <X className="h-2.5 w-2.5" />
                              </Button>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-popover text-popover-foreground text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                <div className="font-medium truncate max-w-32">{report.file?.name}</div>
                                <div className="text-muted-foreground">{language === 'ar' ? report.providerNameAr || report.providerName : report.providerName}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Preview Area */}
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-800 dark:bg-gray-900">
                    {previewUrl ? (
                      <>
                        {isImageUrl(previewUrl) ? (
                          <div className="w-full h-full overflow-auto">
                            <img
                              src={previewUrl}
                              alt="Document Preview"
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <iframe
                            src={previewUrl}
                            className="w-full h-full bg-gray-800"
                            title="Document Preview"
                            style={{ colorScheme: 'dark' }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t('dashboard.coordinator.selectReport')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="invoices" className="h-[72vh]">
                <div className="flex gap-4 h-full">
                  {/* Invoices List */}
                  <div className="w-96 flex-shrink-0 border rounded-lg p-3 space-y-2 overflow-y-auto flex flex-col">
                    {/* Header with Add Invoice Button */}
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        {t('dashboard.coordinator.treatmentInvoices')}
                      </h4>
                      {selectedRequest.status === 'treatment_started' && (
                        <InvoiceUploader
                          invoices={pendingInvoices}
                          onInvoicesChange={setPendingInvoices}
                          translationPrefix="dashboard.coordinator.form"
                          nationalId={selectedRequest.nationalId}
                        />
                      )}
                    </div>
                    
                    {/* Invoices with details */}
                    <div className="flex-1 space-y-2 overflow-y-auto">
                      {selectedRequest.invoices?.map((invoice, i) => (
                        <div
                          key={`inv-${i}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === invoice.url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                          onClick={() => setPreviewUrl(invoice.url)}
                        >
                          <div className="flex gap-3">
                            <div className="min-w-0 flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 flex-shrink-0" />
                                  <span className="font-medium text-sm">{invoice.invoiceNumber}</span>
                                </div>
                                <span className="font-medium text-sm inline-flex items-center gap-1">{((invoice.totalAfterDiscount || 0) + (invoice.taxAmount || 0)).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></span>
                              </div>
                              {(invoice.providerName || invoice.providerNameAr || invoice.providerId) && (
                                <p className={`text-sm truncate ${previewUrl === invoice.url ? 'text-[var(--namat-green)]' : 'opacity-70'}`}>{(() => {
                                  const p = activeProviders.find(pr => pr.id === invoice.providerId);
                                  return language === 'ar'
                                    ? (invoice.providerNameAr || p?.nameAr || invoice.providerName)
                                    : (invoice.providerName || p?.name || invoice.providerNameAr);
                                })()}</p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={invoice.url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); setEditingFileIndex(i); setEditingFileType('invoice'); }}
                              >
                                <Edit className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Legacy invoices (without details) */}
                      {!selectedRequest.invoices?.length && selectedRequest.invoiceUrls?.map((url, i) => (
                        <div
                          key={`inv-legacy-${i}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                          onClick={() => setPreviewUrl(url)}
                        >
                          <div className="flex gap-3">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <FileText className="h-4 w-4 flex-shrink-0" />
                              <span className="text-sm">{t('dashboard.coordinator.invoice')} {i + 1}</span>
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <a
                                href={url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-2.5 w-2.5" />
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {!selectedRequest.invoices?.length && !selectedRequest.invoiceUrls?.length && (
                        <p className="text-sm text-muted-foreground">{t('dashboard.coordinator.noInvoices')}</p>
                      )}
                    </div>

                    {/* Upload Pending Invoices Button */}
                    {pendingInvoices.length > 0 && selectedRequest.status === 'treatment_started' && (
                      <div className="border-t mt-3 pt-3 space-y-2">
                        <Button
                          size="sm"
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={handleAddInvoiceFromDetails}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                          {t('dashboard.coordinator.uploadInvoiceBtn')} ({pendingInvoices.length})
                        </Button>
                        {/* Pending Invoices Thumbnails */}
                        <div className="flex flex-wrap gap-2">
                          {pendingInvoices.map((invoice, index) => (
                            <div key={index} className="relative group">
                              <div className="w-12 h-12 rounded-lg border overflow-hidden bg-muted">
                                {invoice.thumbnailUrl ? (
                                  invoice.file.type === 'application/pdf' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-muted">
                                      <FileText className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                  ) : (
                                    <img src={invoice.thumbnailUrl} alt={invoice.file.name} className="w-full h-full object-cover" />
                                  )
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-muted">
                                    <FileText className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute -top-1 -right-1 h-4 w-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => setPendingInvoices(prev => prev.filter((_, i) => i !== index))}
                              >
                                <X className="h-2.5 w-2.5" />
                              </Button>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-popover text-popover-foreground text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                <div className="font-medium">{invoice.invoiceNumber}</div>
                                <div className="text-muted-foreground">{(() => {
                                  const p = activeProviders.find(pr => pr.id === invoice.providerId);
                                  return language === 'ar'
                                    ? (invoice.providerNameAr || p?.nameAr || invoice.providerName)
                                    : (invoice.providerName || p?.name || invoice.providerNameAr);
                                })()}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Invoices Total */}
                    {selectedRequest.invoices && selectedRequest.invoices.length > 0 && (
                      <div className="border-t mt-3 pt-3">
                        <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-sm">{t('dashboard.coordinator.form.grandTotal')}</span>
                            <span className="font-bold text-primary text-lg inline-flex items-center gap-1">
                              {selectedRequest.invoices.reduce((sum, inv) => sum + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-5 w-5" />
                            </span>
                          </div>
                          {selectedRequest.invoices.reduce((sum, inv) => sum + (inv.taxAmount || 0), 0) > 0 && (
                            <div className="flex justify-between items-center text-muted-foreground">
                              <span className="text-xs">{t('common.vatIncluded')}</span>
                              <span className="text-xs inline-flex items-center gap-1">
                                {selectedRequest.invoices.reduce((sum, inv) => sum + (inv.taxAmount || 0), 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Preview Area */}
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-800 dark:bg-gray-900">
                    {previewUrl ? (
                      <>
                        {isImageUrl(previewUrl) ? (
                          <div className="w-full h-full overflow-auto">
                            <img
                              src={previewUrl}
                              alt="Invoice Preview"
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <iframe
                            src={previewUrl}
                            className="w-full h-full bg-gray-800"
                            title="Invoice Preview"
                            style={{ colorScheme: 'dark' }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t('dashboard.coordinator.selectInvoice')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Payment Slips Tab */}
              <TabsContent value="paymentSlips" className="h-[72vh]">
                <div className="flex gap-4 h-full">
                  {/* Payment Slips List */}
                  <div className="w-80 flex-shrink-0 border rounded-lg p-3 space-y-2 overflow-y-auto">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
                      {t('dashboard.coordinator.paymentSlips')}
                    </h4>
                    {selectedRequest.paymentSlipUrls?.map((url, i) => (
                      <div
                        key={`slip-${i}`}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${previewUrl === url ? 'bg-[var(--namat-text)] text-white border-[var(--namat-text)]' : 'hover:bg-muted/50'}`}
                        onClick={() => setPreviewUrl(url)}
                      >
                        <div className="flex gap-3">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <FileText className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">{t('dashboard.coordinator.paymentSlip')} {i + 1}</span>
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <a
                              href={url}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--namat-green)] bg-[var(--namat-green)] text-white hover:bg-[var(--namat-green)]/80 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Download className="h-2.5 w-2.5" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                    {!selectedRequest.paymentSlipUrls?.length && (
                      <p className="text-sm text-muted-foreground">{t('dashboard.coordinator.noPaymentSlips')}</p>
                    )}
                  </div>
                  
                  {/* Preview Area */}
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-800 dark:bg-gray-900">
                    {previewUrl ? (
                      <>
                        {isImageUrl(previewUrl) ? (
                          <div className="w-full h-full overflow-auto">
                            <img
                              src={previewUrl}
                              alt="Payment Slip Preview"
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <iframe
                            src={previewUrl}
                            className="w-full h-full bg-gray-800"
                            title="Payment Slip Preview"
                            style={{ colorScheme: 'dark' }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <div className="text-center">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t('dashboard.coordinator.selectPaymentSlip')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Updates Tab */}
              <TabsContent value="updates" className="max-h-[72vh] overflow-y-auto">
                <div className="space-y-4">
                  {/* Header with Add Button */}
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      {t('dashboard.coordinator.updates.title')}
                    </h4>
                    <Button onClick={() => setShowAddUpdate(true)} className="bg-primary">
                      <Plus className="h-4 w-4 me-2" />
                      {t('dashboard.coordinator.updates.addUpdate')}
                    </Button>
                  </div>

                  {/* Updates List */}
                  <div className="space-y-3">
                    {selectedRequest.updates && selectedRequest.updates.length > 0 ? (
                      [...selectedRequest.updates].reverse().map((update) => (
                        <div key={update.id} className="p-4 border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{update.createdByName}</span>
                              <span className="text-xs text-muted-foreground">({update.createdByRole})</span>
                            </div>
                            <Badge className={STATUS_COLORS[update.statusAtTime]}>
                              {t(`dashboard.coordinator.statuses.${update.statusAtTime}`)}
                            </Badge>
                          </div>
                          <p className="text-sm" dir={dir}>{update.content}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatLocalDate(update.createdAt instanceof Date ? update.createdAt : (update.createdAt as any)?.toDate?.() || new Date(update.createdAt), 'PPpp')}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        {t('dashboard.coordinator.updates.noUpdates')}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Add Update Modal */}
              <Dialog open={showAddUpdate} onOpenChange={(open) => {
                setShowAddUpdate(open);
                if (!open) setNewUpdateText('');
              }}>
                <DialogContent className="max-w-lg" dir={dir}>
                  <DialogHeader>
                    <DialogTitle>{t('dashboard.coordinator.updates.addUpdate')}</DialogTitle>
                    <DialogDescription>
                      {t('dashboard.coordinator.updates.title')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>{t('dashboard.coordinator.updates.placeholder')}</Label>
                      <Textarea
                        value={newUpdateText}
                        onChange={(e) => setNewUpdateText(e.target.value)}
                        placeholder={t('dashboard.coordinator.updates.placeholder')}
                        dir={dir}
                        rows={4}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddUpdate(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button 
                      onClick={handleAddUpdate} 
                      disabled={!newUpdateText.trim() || isSubmitting}
                    >
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                      {t('dashboard.coordinator.updates.addBtn')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Case Modal */}
      <Dialog open={isReviewOpen} onOpenChange={setIsReviewOpen}>
        <DialogContent className="max-w-lg" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.coordinator.reviewCase')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.coordinator.reviewDescription')}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p><strong>{t('dashboard.coordinator.form.applicantName')}:</strong> {selectedRequest.applicantName}</p>
                <p><strong>{t('dashboard.coordinator.form.condition')}:</strong> {selectedRequest.medicalCondition}</p>
                <p className="inline-flex items-center gap-1"><strong>{t('dashboard.coordinator.form.cost')}:</strong> {getNetMedicalCost(selectedRequest).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="socialStatus"
                    checked={reviewData.socialStatusFits}
                    onCheckedChange={(checked) => setReviewData(prev => ({ ...prev, socialStatusFits: !!checked }))}
                  />
                  <Label htmlFor="socialStatus" className="cursor-pointer">
                    {t('dashboard.coordinator.reviewForm.socialStatusFits')}
                  </Label>
                </div>
                
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="medicalCondition"
                    checked={reviewData.medicalConditionFits}
                    onCheckedChange={(checked) => setReviewData(prev => ({ ...prev, medicalConditionFits: !!checked }))}
                  />
                  <Label htmlFor="medicalCondition" className="cursor-pointer">
                    {t('dashboard.coordinator.reviewForm.medicalConditionFits')}
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reviewNotes">{t('dashboard.coordinator.reviewForm.notes')}</Label>
                <Textarea
                  id="reviewNotes"
                  value={reviewData.coordinatorNotes}
                  onChange={(e) => setReviewData(prev => ({ ...prev, coordinatorNotes: e.target.value }))}
                  dir={dir}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsReviewOpen(false)} 
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={handleReviewCase} 
                  disabled={isSubmitting}
                  className={reviewData.socialStatusFits && reviewData.medicalConditionFits 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                  }
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {reviewData.socialStatusFits && reviewData.medicalConditionFits 
                    ? t('dashboard.coordinator.reviewForm.approve')
                    : t('dashboard.coordinator.reviewForm.reject')
                  }
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* File Details/Edit Dialog */}
      <Dialog open={editingFileIndex !== null} onOpenChange={(open) => { if (!open) { setEditingFileIndex(null); setEditingFileType(null); setIsEditingInvoice(false); setIsEditingReport(false); setIsEditingProgressReport(false); } }}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>{isEditingInvoice ? t('dashboard.coordinator.editInvoice') : (isEditingReport || isEditingProgressReport) ? t('dashboard.coordinator.editReport') : t('common.fileDetails')}</DialogTitle>
          </DialogHeader>
          {selectedRequest && editingFileIndex !== null && editingFileType === 'report' && (() => {
            const report = selectedRequest.medicalReports?.[editingFileIndex];
            if (!report) return null;

            if (isEditingReport) {
              const selectedProvider = activeProviders.find(p => p.id === editingReportData.providerId);
              return (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-sm">{t('dashboard.coordinator.form.provider')} *</Label>
                      <Select
                        value={editingReportData.providerId}
                        onValueChange={(val) => {
                          setEditingReportData(prev => ({ ...prev, providerId: val, department: '' }));
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder={t('dashboard.coordinator.form.selectProvider')} /></SelectTrigger>
                        <SelectContent>
                          {activeProviders.map((provider) => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {language === 'ar' ? (provider.nameAr || provider.name) : provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedProvider && selectedProvider.departments.length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-sm">{t('common.department')}</Label>
                        <Select
                          value={editingReportData.department}
                          onValueChange={(val) => setEditingReportData(prev => ({ ...prev, department: val }))}
                        >
                          <SelectTrigger><SelectValue placeholder={t('dashboard.coordinator.form.selectDepartment')} /></SelectTrigger>
                          <SelectContent>
                            {selectedProvider.departments.map((dept) => (
                              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-sm">{t('common.reportDate')}</Label>
                      <Input
                        type="date"
                        value={editingReportData.reportDate}
                        onChange={(e) => setEditingReportData(prev => ({ ...prev, reportDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t">
                    <Button size="sm" onClick={handleUpdateReportMetadata} disabled={isSubmitting || !editingReportData.providerId}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Check className="h-4 w-4 me-2" />}
                      {t('common.save')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingReport(false)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.fileName')}</span>
                    <span className="text-sm font-medium truncate max-w-[60%] text-end">{report.name}</span>
                  </div>
                  {(report.providerName || report.providerId) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.provider')}</span>
                      <span className="text-sm font-medium">{(() => {
                        const p = activeProviders.find(pr => pr.id === report.providerId);
                        return language === 'ar'
                          ? (p?.nameAr || report.providerName)
                          : (report.providerName || p?.name);
                      })()}</span>
                    </div>
                  )}
                  {report.department && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.department')}</span>
                      <span className="text-sm font-medium">{report.department}</span>
                    </div>
                  )}
                  {report.reportDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.reportDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(report.reportDate))}</span>
                    </div>
                  )}
                  {report.uploadedAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.uploadDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(report.uploadedAt))}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <a
                      href={report.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      {t('common.download')}
                    </a>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const dateStr = report.reportDate ? (() => { const d = new Date(report.reportDate); return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0]; })() : '';
                        setEditingReportData({
                          providerId: report.providerId || '',
                          department: report.department || '',
                          reportDate: dateStr,
                        });
                        setIsEditingReport(true);
                      }}
                    >
                      <Pencil className="h-4 w-4 me-2" />
                      {t('common.edit')}
                    </Button>
                  </div>
                  {selectedRequest.status !== 'completed' && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        handleDeleteReport(editingFileIndex);
                        setEditingFileIndex(null);
                        setEditingFileType(null);
                      }}
                    >
                      <Trash2 className="h-4 w-4 me-2" />
                      {t('common.deleteFile')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
          {selectedRequest && editingFileIndex !== null && editingFileType === 'progressReport' && (() => {
            const report = selectedRequest.progressReports?.[editingFileIndex];
            if (!report) return null;

            if (isEditingProgressReport) {
              return (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-sm">{t('dashboard.coordinator.form.provider')} *</Label>
                      <Select
                        value={editingProgressReportData.providerId}
                        onValueChange={(val) => setEditingProgressReportData(prev => ({ ...prev, providerId: val }))}
                      >
                        <SelectTrigger><SelectValue placeholder={t('dashboard.coordinator.form.selectProvider')} /></SelectTrigger>
                        <SelectContent>
                          {activeProviders.map((provider) => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {language === 'ar' ? (provider.nameAr || provider.name) : provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">{t('common.department')}</Label>
                      <Input
                        value={editingProgressReportData.department}
                        onChange={(e) => setEditingProgressReportData(prev => ({ ...prev, department: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">{t('common.reportDate')}</Label>
                      <Input
                        type="date"
                        value={editingProgressReportData.reportDate}
                        onChange={(e) => setEditingProgressReportData(prev => ({ ...prev, reportDate: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">{t('common.progressReports.modal.notes')}</Label>
                      <Textarea
                        rows={3}
                        value={editingProgressReportData.notes}
                        onChange={(e) => setEditingProgressReportData(prev => ({ ...prev, notes: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t">
                    <Button size="sm" onClick={handleUpdateProgressReportMetadata} disabled={isSubmitting || !editingProgressReportData.providerId}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Check className="h-4 w-4 me-2" />}
                      {t('common.save')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingProgressReport(false)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.fileName')}</span>
                    <span className="text-sm font-medium truncate max-w-[60%] text-end">{report.name}</span>
                  </div>
                  {(report.providerName || report.providerId) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.provider')}</span>
                      <span className="text-sm font-medium">{(() => {
                        const p = activeProviders.find(pr => pr.id === report.providerId);
                        return language === 'ar'
                          ? (p?.nameAr || report.providerName)
                          : (report.providerName || p?.name);
                      })()}</span>
                    </div>
                  )}
                  {report.department && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.department')}</span>
                      <span className="text-sm font-medium">{report.department}</span>
                    </div>
                  )}
                  {report.reportDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.reportDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(report.reportDate))}</span>
                    </div>
                  )}
                  {report.uploadedAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.uploadDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(report.uploadedAt))}</span>
                    </div>
                  )}
                  {report.notes && (
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-sm text-muted-foreground">{t('common.progressReports.modal.notes')}</span>
                      <span className="text-sm font-medium text-end whitespace-pre-wrap">{report.notes}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <a
                      href={report.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      {t('common.download')}
                    </a>
                    {['approved_treatment', 'treatment_started'].includes(selectedRequest.status) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const dateStr = report.reportDate ? (() => { const d = new Date(report.reportDate); return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0]; })() : '';
                          setEditingProgressReportData({
                            providerId: report.providerId || '',
                            department: report.department || '',
                            reportDate: dateStr,
                            notes: report.notes || '',
                          });
                          setIsEditingProgressReport(true);
                        }}
                      >
                        <Pencil className="h-4 w-4 me-2" />
                        {t('common.edit')}
                      </Button>
                    )}
                  </div>
                  {['approved_treatment', 'treatment_started'].includes(selectedRequest.status) && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        handleDeleteProgressReport(editingFileIndex);
                        setEditingFileIndex(null);
                        setEditingFileType(null);
                      }}
                    >
                      <Trash2 className="h-4 w-4 me-2" />
                      {t('common.deleteFile')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
          {selectedRequest && editingFileIndex !== null && editingFileType === 'invoice' && (() => {
            const invoice = selectedRequest.invoices?.[editingFileIndex];
            if (!invoice) return null;

            if (isEditingInvoice) {
              const nationalId = selectedRequest.idNumber || selectedRequest.nationalId;
              const vatApplies = nationalId ? !nationalId.startsWith('1') : false;
              const VAT_RATE = 0.15;
              return (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label>{t('dashboard.coordinator.form.provider')} *</Label>
                      <Select
                        value={editingInvoiceData.providerId}
                        onValueChange={(val) => {
                          const provider = activeProviders.find(p => p.id === val);
                          setEditingInvoiceData(prev => ({
                            ...prev,
                            providerId: val,
                            providerName: provider?.name || '',
                            vatNumber: provider?.vatNumber || '',
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('dashboard.coordinator.form.selectProvider')} />
                        </SelectTrigger>
                        <SelectContent>
                          {activeProviders.map((provider) => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {language === 'ar' ? (provider.nameAr || provider.name) : provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>{t('dashboard.coordinator.form.vatNumber')}</Label>
                      <Input value={editingInvoiceData.vatNumber} readOnly disabled className="bg-muted" dir="ltr" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label>{t('common.invoiceDate')} *</Label>
                        <Input
                          type="date"
                          value={editingInvoiceData.invoiceDate}
                          onChange={(e) => setEditingInvoiceData(prev => ({ ...prev, invoiceDate: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>{t('common.invoiceNumber')} *</Label>
                        <Input
                          value={editingInvoiceData.invoiceNumber}
                          onChange={(e) => setEditingInvoiceData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                          dir="ltr"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label>{t('common.totalBeforeDiscount')} *</Label>
                        <Input
                          type="number"
                          value={editingInvoiceData.totalBeforeDiscount}
                          onChange={(e) => setEditingInvoiceData(prev => ({ ...prev, totalBeforeDiscount: e.target.value }))}
                          placeholder="0.00"
                          dir="ltr"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>{t('common.totalAfterDiscount')} *</Label>
                        <Input
                          type="number"
                          value={editingInvoiceData.totalAfterDiscount}
                          onChange={(e) => {
                            const newTotal = e.target.value;
                            const totalN = Number(newTotal) || 0;
                            const clampedNonTax = Math.min(Number(editingInvoiceData.nonTaxableAmount) || 0, totalN);
                            const newTaxable = String(Math.max(0, totalN - clampedNonTax));
                            const tax = vatApplies ? ((Number(newTaxable) || 0) * VAT_RATE).toFixed(2) : '0';
                            setEditingInvoiceData(prev => ({ ...prev, totalAfterDiscount: newTotal, nonTaxableAmount: String(clampedNonTax), taxableAmount: newTaxable, taxAmount: tax }));
                          }}
                          placeholder="0.00"
                          dir="ltr"
                        />
                      </div>
                    </div>
                    {vatApplies && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label>{t('common.nonTaxableAmount')}</Label>
                          <Input
                            type="number"
                            value={editingInvoiceData.nonTaxableAmount}
                            onChange={(e) => {
                              const total = Number(editingInvoiceData.totalAfterDiscount) || 0;
                              const clamped = Math.min(Math.max(0, Number(e.target.value) || 0), total);
                              const newTaxable = String(Math.max(0, total - clamped));
                              const tax = ((Number(newTaxable) || 0) * VAT_RATE).toFixed(2);
                              setEditingInvoiceData(prev => ({ ...prev, nonTaxableAmount: String(clamped), taxableAmount: newTaxable, taxAmount: tax }));
                            }}
                            placeholder="0.00"
                            dir="ltr"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>{t('common.taxableAmount')}</Label>
                          <Input
                            type="number"
                            value={editingInvoiceData.taxableAmount}
                            onChange={(e) => {
                              const total = Number(editingInvoiceData.totalAfterDiscount) || 0;
                              const clamped = Math.min(Math.max(0, Number(e.target.value) || 0), total);
                              const newNonTax = String(Math.max(0, total - clamped));
                              const tax = (clamped * VAT_RATE).toFixed(2);
                              setEditingInvoiceData(prev => ({ ...prev, taxableAmount: String(clamped), nonTaxableAmount: newNonTax, taxAmount: tax }));
                            }}
                            placeholder="0.00"
                            dir="ltr"
                          />
                        </div>
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label>{t('common.taxAmount')} {vatApplies ? '(15%)' : ''}</Label>
                      <Input type="number" value={editingInvoiceData.taxAmount} readOnly disabled className="bg-muted" dir="ltr" />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('common.grandTotal')}</Label>
                      <Input
                        type="number"
                        value={((Number(editingInvoiceData.totalAfterDiscount) || 0) + (Number(editingInvoiceData.taxAmount) || 0)).toFixed(2)}
                        readOnly
                        disabled
                        className="bg-muted font-medium"
                        dir="ltr"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t">
                    <Button type="button" variant="outline" size="sm" onClick={() => setIsEditingInvoice(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleUpdateInvoiceMetadata}
                      disabled={isSubmitting || !editingInvoiceData.providerId || !editingInvoiceData.invoiceDate || !editingInvoiceData.invoiceNumber || !editingInvoiceData.totalBeforeDiscount || !editingInvoiceData.totalAfterDiscount}
                    >
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <CheckCircle className="h-4 w-4 me-2" />}
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.invoiceNumber')}</span>
                    <span className="text-sm font-medium">{invoice.invoiceNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.fileName')}</span>
                    <span className="text-sm font-medium truncate max-w-[60%] text-end">{invoice.name}</span>
                  </div>
                  {(invoice.providerName || invoice.providerNameAr || invoice.providerId) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.provider')}</span>
                      <span className="text-sm font-medium">{(() => {
                        const p = activeProviders.find(pr => pr.id === invoice.providerId);
                        return language === 'ar'
                          ? (invoice.providerNameAr || p?.nameAr || invoice.providerName)
                          : (invoice.providerName || p?.name || invoice.providerNameAr);
                      })()}</span>
                    </div>
                  )}
                  {invoice.invoiceDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.invoiceDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(invoice.invoiceDate))}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.totalBeforeDiscount')}</span>
                    <span className="text-sm font-medium inline-flex items-center gap-1">{invoice.totalBeforeDiscount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('common.totalAfterDiscount')}</span>
                    <span className="text-sm font-medium inline-flex items-center gap-1">{invoice.totalAfterDiscount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                  </div>
                  {invoice.taxableAmount != null && invoice.taxableAmount !== invoice.totalAfterDiscount && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.taxableAmount')}</span>
                      <span className="text-sm font-medium inline-flex items-center gap-1">{invoice.taxableAmount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                    </div>
                  )}
                  {invoice.taxAmount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.taxAmount')}</span>
                      <span className="text-sm font-medium inline-flex items-center gap-1">{invoice.taxAmount?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                    </div>
                  )}
                  {invoice.uploadedAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.uploadDate')}</span>
                      <span className="text-sm font-medium">{formatLocalDate(new Date(invoice.uploadedAt))}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <a
                    href={invoice.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Download className="h-4 w-4" />
                    {t('common.download')}
                  </a>
                  {/* Edit is always available so a coordinator can correct
                      invoice figures (including the VAT split) even after
                      the case is completed. */}
                  {true && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const dateStr = invoice.invoiceDate
                          ? new Date(invoice.invoiceDate).toISOString().split('T')[0]
                          : '';
                        setEditingInvoiceData({
                          providerId: invoice.providerId || '',
                          providerName: invoice.providerName || '',
                          vatNumber: invoice.vatNumber || '',
                          invoiceDate: dateStr,
                          invoiceNumber: invoice.invoiceNumber || '',
                          totalBeforeDiscount: String(invoice.totalBeforeDiscount || ''),
                          totalAfterDiscount: String(invoice.totalAfterDiscount || ''),
                          taxableAmount: String(invoice.taxableAmount ?? invoice.totalAfterDiscount ?? ''),
                          nonTaxableAmount: String((invoice as any).nonTaxableAmount ?? Math.max(0, (invoice.totalAfterDiscount || 0) - (invoice.taxableAmount ?? invoice.totalAfterDiscount ?? 0))),
                          taxAmount: String(invoice.taxAmount || ''),
                        });
                        setIsEditingInvoice(true);
                      }}
                    >
                      <Pencil className="h-4 w-4 me-2" />
                      {t('common.edit')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Invoice Upload Modal */}
      <Dialog open={isInvoiceOpen} onOpenChange={setIsInvoiceOpen}>
        <DialogContent className="max-w-2xl" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.coordinator.uploadInvoices')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.coordinator.uploadInvoicesDescription')}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p><strong>{t('dashboard.coordinator.form.nameArabic')}:</strong> {selectedRequest.nameArabic || selectedRequest.applicantName}</p>
                <p className="inline-flex items-center gap-1"><strong>{t('dashboard.coordinator.approvedAmount')}:</strong> {(selectedRequest.approvedAmount || selectedRequest.cost)?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></p>
              </div>

              <div className="space-y-2">
                <Label>{t('dashboard.coordinator.treatmentInvoices')}</Label>
                <InvoiceUploader
                  invoices={pendingInvoices}
                  onInvoicesChange={setPendingInvoices}
                  translationPrefix="dashboard.coordinator.form"
                  nationalId={selectedRequest?.nationalId}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setIsInvoiceOpen(false); setPendingInvoices([]); }} 
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={handleUploadInvoices} 
                  disabled={isSubmitting || pendingInvoices.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {t('dashboard.coordinator.uploadInvoicesBtn')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <CoordinatorSettings 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
      />

      {/* Fullscreen ID Document Preview */}
      {idPreviewUrl && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/90"
          style={{ pointerEvents: 'auto', zIndex: 99999, isolation: 'isolate' }}
          onClick={isCropping ? undefined : () => { setIdPreviewUrl(null); setIdPreviewRotation(0); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { if (isCropping) { setIsCropping(false); setCropRect(null); } else { setIdPreviewUrl(null); setIdPreviewRotation(0); } } }}
          tabIndex={0}
          ref={(el) => { if (el && !el.dataset.focused) { el.dataset.focused = 'true'; el.focus(); } }}
        >
          <button
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={(e) => { e.stopPropagation(); if (isCropping) { setIsCropping(false); setCropRect(null); } else { setIdPreviewUrl(null); setIdPreviewRotation(0); } }}
          >
            <X className="w-6 h-6" />
          </button>
          {!isPdfUrl(idPreviewUrl) && (
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
              {/* Save rotation */}
              {idPreviewRotation !== 0 && !isCropping && (
                <button
                  className="h-10 px-4 flex items-center justify-center gap-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
                  onClick={(e) => { e.stopPropagation(); handleSaveRotation(); }}
                  disabled={isSavingRotation}
                >
                  {isSavingRotation ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {t('common.saveRotation')}
                </button>
              )}
              {/* Save crop */}
              {isCropping && cropRect && cropRect.w > 1 && cropRect.h > 1 && (
                <>
                  <button
                    className="h-10 px-4 flex items-center justify-center gap-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
                    onClick={(e) => { e.stopPropagation(); handleSaveCrop(); }}
                    disabled={isSavingRotation}
                  >
                    {isSavingRotation ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {t('common.saveCrop')}
                  </button>
                  <button
                    className="h-10 px-4 flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsCropping(false); setCropRect(null); }}
                  >
                    <XCircle className="w-4 h-4" />
                    {t('common.cancelCrop')}
                  </button>
                </>
              )}
              {/* Tool buttons */}
              {!isCropping && (
                <>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIdPreviewRotation(prev => (prev + 90) % 360); }}
                    disabled={isSavingRotation}
                    title={t('common.rotateRight')}
                  >
                    <RotateCw className="w-5 h-5" />
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIdPreviewRotation(prev => (prev + 270) % 360); }}
                    disabled={isSavingRotation}
                    title={t('common.rotateLeft')}
                  >
                    <RotateCcw className="w-5 h-5" />
                  </button>
                  <div className="w-px h-6 bg-white/20 mx-1" />
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsCropping(true); setIdPreviewRotation(0); }}
                    disabled={isSavingRotation}
                    title={t('common.crop')}
                  >
                    <Crop className="w-5 h-5" />
                  </button>
                </>
              )}
              {isCropping && !cropRect && (
                <span className="text-white/70 text-sm select-none">{t('common.crop')}: drag to select</span>
              )}
            </div>
          )}
          {!isCropping && (
            <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2">
              <a
                href={idPreviewUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                onClick={(e) => e.stopPropagation()}
                title={t('common.download')}
              >
                <Download className="w-5 h-5" />
              </a>
              <a
                href={idPreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                onClick={(e) => e.stopPropagation()}
                title={t('common.openInNewTab')}
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          )}
          {isPdfUrl(idPreviewUrl) ? (
            <iframe
              src={idPreviewUrl}
              className="w-[90vw] h-[90vh] rounded-lg bg-white"
              onClick={(e) => e.stopPropagation()}
            />
          ) : isCropping ? (
            <div
              ref={cropContainerRef}
              className="relative select-none"
              style={{ maxWidth: '95vw', maxHeight: '95vh' }}
              onPointerDown={onCropPointerDown}
              onPointerMove={onCropPointerMove}
              onPointerUp={onCropPointerUp}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={idPreviewUrl} className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl" draggable={false} />
              {cropRect && (
                <>
                  <div className="absolute left-0 right-0 top-0 bg-black/60 pointer-events-none" style={{ height: `${cropRect.y}%` }} />
                  <div className="absolute left-0 right-0 bottom-0 bg-black/60 pointer-events-none" style={{ height: `${100 - cropRect.y - cropRect.h}%` }} />
                  <div className="absolute left-0 bg-black/60 pointer-events-none" style={{ top: `${cropRect.y}%`, height: `${cropRect.h}%`, width: `${cropRect.x}%` }} />
                  <div className="absolute right-0 bg-black/60 pointer-events-none" style={{ top: `${cropRect.y}%`, height: `${cropRect.h}%`, width: `${100 - cropRect.x - cropRect.w}%` }} />
                  <div className="absolute border-2 border-white/90 pointer-events-none" style={{ left: `${cropRect.x}%`, top: `${cropRect.y}%`, width: `${cropRect.w}%`, height: `${cropRect.h}%` }}>
                    <div className="absolute inset-0">
                      <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
                      <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
                      <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
                      <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
                    </div>
                  </div>
                  {['tl', 'tr', 'bl', 'br', 't', 'b', 'l', 'r'].map(h => (
                    <div key={h} className="bg-white rounded-full shadow-md border border-gray-300 z-10" style={getCropHandleStyle(h, cropRect)} onPointerDown={(e) => onCropHandleDown(h, e)} />
                  ))}
                </>
              )}
            </div>
          ) : (
            <img
              src={idPreviewUrl}
              className="max-w-[95vw] max-h-[95vh] object-contain select-none rounded-lg shadow-2xl transition-transform duration-300"
              style={{ transform: `rotate(${idPreviewRotation}deg)` }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>,
        document.body
      )}

      {/* Start-treatment confirm prompt (shown after the first progress report upload) */}
      <Dialog open={!!startPromptRequest} onOpenChange={(open) => { if (!open) setStartPromptRequest(null); }}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.coordinator.startTreatmentPromptTitle')}</DialogTitle>
            <DialogDescription>{t('dashboard.coordinator.startTreatmentPromptDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartPromptRequest(null)}>
              {t('dashboard.coordinator.startTreatmentPromptCancel')}
            </Button>
            <Button onClick={async () => {
              const req = startPromptRequest;
              setStartPromptRequest(null);
              if (req) await handleStartTreatment(req);
            }}>
              {t('dashboard.coordinator.startTreatmentPromptConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
